// Release notes history — newest entry first.
// To trigger the modal for all users: add a new entry at index 0 (and keep
// package.json version in step). This file is the single source of truth for
// what the "What's New" modal shows — the modal logic keys off the entries
// here, not off any env var, so it works identically in dev and production.
// Optional `note` renders as a closing callout under the feature list.
const releaseNotes = [
  {
    version: "0.45.0",
    date: "November 2026",
    features: [
      {
        heading: "Charts In Ask Cadenza",
        description:
          "Ask Cadenza can now draw a chart in its answer, from one run, a " +
          "comparison of runs, or a closure run. The numbers are read directly " +
          "from your results, shown in your own units, and can be downloaded " +
          "as a CSV.",
      },
      {
        heading: "Excel Exports From A Conversation",
        description:
          "Ask for a workbook and Ask Cadenza offers a card that opens the " +
          "export wizard on that run. Nothing is created until you generate it " +
          "yourself.",
      },
      {
        heading: "Find Past Conversations",
        description:
          "Ask Cadenza's History can now be searched by question or scenario " +
          "name, and each conversation shows the scenario it started on.",
      },
      {
        heading: "Ask Cadenza Usage Report",
        description:
          "Company administrators can see what Ask Cadenza cost each month, by " +
          "person, project and model, on the AI Assistant tab. It shows costs " +
          "and counts only, never what anyone asked.",
      },
    ],
  },
  {
    version: "0.44.4",
    date: "November 2026",
    features: [
      {
        heading: "Ask Cadenza And Your Data",
        description:
          "The Ask Cadenza Help chapter now explains whether your data is used " +
          "for AI training, how long it's kept, and what changes if you connect " +
          "Cadenza to another AI app.",
      },
    ],
  },
  {
    version: "0.44.3",
    date: "November 2026",
    features: [
      {
        heading: "Jump To What Ask Cadenza Read",
        description:
          "Each step listed under an Ask Cadenza answer now links to the page " +
          "it came from, so you can check the numbers yourself. It opens the " +
          "right scenario and, where it can, the run or heap too.",
      },
      {
        heading: "A Heads-Up Before The Budget Runs Out",
        description:
          "Company administrators now get one email a month when Ask Cadenza " +
          "reaches 80% of the company's monthly budget.",
      },
      {
        heading: "Ask Cadenza In The Help Center",
        description:
          "A new Help chapter covers what Ask Cadenza can and can't do, what " +
          "it sends to Anthropic, how usage is counted and how a company sets " +
          "it up.",
      },
      {
        heading: "Conversations Stay Put",
        description:
          "Fixed Ask Cadenza clearing the conversation, and sometimes cutting " +
          "off an answer, during a long session.",
      },
    ],
  },
  {
    version: "0.44.2",
    date: "November 2026",
    features: [
      {
        heading: "Choose Ask Cadenza's Model",
        description:
          "Pick which Claude model answers from the top of the Ask Cadenza " +
          "panel — your company's model or any faster, cheaper one. Your " +
          "company's administrators set the most capable model available " +
          "under Company › AI Assistant.",
      },
    ],
  },
  {
    version: "0.44.1",
    date: "November 2026",
    features: [
      {
        heading: "Ask Cadenza Knows What You're Looking At",
        description:
          "Ask about \"this run\" or \"this heap\" and Ask Cadenza now knows " +
          "which one you mean — it sees the page you're on and the scenario, " +
          "run, heap or snapshot date in front of you.",
      },
      {
        heading: "Your Company's Assistant, On Every Project",
        description:
          "Ask Cadenza now comes from your own company, wherever you work. " +
          "If your company has it switched on, you can use it on any project " +
          "you've been given access to, including other companies' projects, " +
          "and usage counts towards your company's budget. The panel shows " +
          "which company is providing it.",
      },
    ],
    note:
      "A project's owner can still stop AI tools from reading its data — " +
      "Company › Settings › AI access.",
  },
  {
    version: "0.44.0",
    date: "November 2026",
    features: [
      {
        heading: "Ask Cadenza",
        description:
          "Ask questions about your project in plain language — how a run " +
          "compares with the baseline, why recovery levels off, what's " +
          "scheduled to leach next. Open it from the button at the bottom " +
          "right of any project page. It reads your project's data, shows " +
          "what it looked at, and never changes anything.",
      },
      {
        heading: "AI Assistant Settings For Your Company",
        description:
          "Company administrators decide whether Ask Cadenza is available, " +
          "connect your organisation's own Anthropic account, choose the " +
          "model, and set monthly and per-person spending limits under " +
          "Company › AI Assistant.",
      },
    ],
    note:
      "Ask Cadenza is off until a company administrator turns it on. Its " +
      "answers come from your data but are written by an AI — check any " +
      "figure you rely on against the Results page.",
  },
  {
    version: "0.43.14",
    date: "October 2026",
    features: [
      {
        heading: "Animated Preset Switching",
        description:
          "Picking a preset on the Results chart now animates the lines into " +
          "place again. Toggling individual metrics, Raw or the Cumulative / " +
          "Daily view stays instant.",
      },
    ],
  },
  {
    version: "0.43.13",
    date: "October 2026",
    features: [
      {
        heading: "Results Chart Draw-In, Fixed",
        description:
          "The Results chart now really does draw its lines in smoothly when " +
          "a run opens — the previous update didn't fully fix the jump some " +
          "users saw as the chart appeared.",
      },
    ],
  },
  {
    version: "0.43.12",
    date: "October 2026",
    features: [
      {
        heading: "Smooth Results Chart Draw-In",
        description:
          "The Results chart draws its lines in smoothly again when a run " +
          "opens, without the brief flicker some users saw — and switching " +
          "presets, metrics or views stays instant.",
      },
    ],
  },
  {
    version: "0.43.11",
    date: "October 2026",
    features: [
      {
        heading: "Faster Results",
        description:
          "Simulation results open much faster, especially for long runs " +
          "with many ponds and plants, and the Results chart now responds " +
          "instantly when you switch presets, metrics or views. Compare, " +
          "Closure, Ensembles and Reconciliation load their results faster " +
          "too.",
      },
    ],
  },
  {
    version: "0.43.10",
    date: "October 2026",
    features: [
      {
        heading: "Smoother Results Chart Loading",
        description:
          "The Results chart now shows a placeholder while a run's data " +
          "loads and draws once everything is ready, instead of drawing a " +
          "partial chart and redrawing it a moment later.",
      },
    ],
  },
  {
    version: "0.43.9",
    date: "October 2026",
    features: [
      {
        heading: "Favorites in Compare",
        description:
          "The Compare window now opens with a Favorites group at the top of " +
          "the metric list — plant metal recovery, pregnant solution from the " +
          "pad, barren solution and make-up water — one click away for any " +
          "set of runs.",
      },
      {
        heading: "Tidier Results Presets",
        description:
          "Pond Flows now shows just the water flowing in and out of each " +
          "pond, and a new Pond Volumes preset shows how much each pond " +
          "holds. Pond Concentrations has been folded into Tenors, which " +
          "showed the same thing.",
      },
      {
        heading: "Metal Recovery Complete on First Load",
        description:
          "The Results chart now opens with every Metal Recovery series — " +
          "metal placed, recoverable, extracted and plant recovery — without " +
          "having to switch presets and back.",
      },
    ],
  },
  {
    version: "0.43.8",
    date: "October 2026",
    features: [
      {
        heading: "Staying Signed In Through Brief Outages",
        description:
          "If Orecasting's server briefly restarts or your connection drops, " +
          "you now stay signed in — the app waits and reconnects instead of " +
          "sending you back to the sign-in page.",
      },
      {
        heading: "Very Large Heaps Handled Smoothly",
        description:
          "Opening the Run page, or clearing and generating leach cycles " +
          "with the assistant, on a heap with an exceptionally large leach " +
          "schedule could overload the server and briefly interrupt the app " +
          "for everyone. These now run comfortably on the largest heaps.",
      },
    ],
  },
  {
    version: "0.43.7",
    date: "October 2026",
    features: [
      {
        heading: "Steadier Generated Leach Cycles",
        description:
          "When Generate Leach Cycles matched a target flow with trimming " +
          "turned on, the area under leach could fall well short of the " +
          "target early in the period — a dip in stacks under leach and " +
          "flow. Generated schedules now hold the target flow throughout.",
      },
    ],
    note:
      "Schedules generated earlier with flow matching and trimming are not " +
      "changed automatically — regenerate them to remove any dip.",
  },
  {
    version: "0.43.6",
    date: "October 2026",
    features: [
      {
        heading: "Steadier Barren Solution Flow",
        description:
          "On the day a barren pond first started holding solution overnight, " +
          "a simulation could send almost none of that day's barren back to " +
          "the heap and fill the gap with makeup water — a one-day dip and " +
          "spike on the Water Balance charts. Barren now returns to the heap " +
          "every day as it should.",
      },
    ],
    note:
      "New simulation runs return barren to the heap a day sooner than " +
      "before, so barren pond volumes and some flow and recovery figures will " +
      "differ slightly from earlier runs of the same scenario.",
  },
  {
    version: "0.43.5",
    date: "October 2026",
    features: [
      {
        heading: "Accurate Days Under Leach",
        description:
          "In the Leach Cycles Coverage view, a stack whose cells were " +
          "irrigated at the same time could show more days under leach than " +
          "it really had. Each day now counts once per stack. The Coverage " +
          "and Cells views also show stack and cell figures side by side.",
      },
      {
        heading: "Admin Users Table Fits the Screen",
        description:
          "The action buttons on the Users page no longer slip off the right " +
          "edge on smaller screens.",
      },
    ],
    note:
      "Days-under-leach figures for some stacks will be lower than before — " +
      "the earlier figures double-counted overlapping cycles.",
  },
  {
    version: "0.43.4",
    date: "October 2026",
    features: [
      {
        heading: "Smoother 3D Heaps",
        description:
          "The 3D heap viewer now draws only the parts of the heap you can " +
          "see, so large heaps rotate, zoom and respond to the date slider " +
          "and cross-section more smoothly — the view itself looks the same.",
      },
      {
        heading: "More Reliable Heap Views",
        description:
          "Fixed a case where part of a heap could disappear, or stop " +
          "responding to double-clicks, after moving the date slider forward.",
      },
    ],
  },
  {
    version: "0.43.3",
    date: "October 2026",
    features: [
      {
        heading: "Large Heaps Open Without Freezing",
        description:
          "Heaps now load in the background, so the page stays responsive " +
          "while a large heap is downloaded and prepared on the Heaps, Heap " +
          "Editor and Leaching pages.",
      },
      {
        heading: "Faster Return Visits",
        description:
          "Heaps you have opened recently are kept on your computer, so " +
          "reopening one skips the download. A heap that has been rebuilt or " +
          "edited since is always downloaded fresh, and the stored copies are " +
          "removed when you sign out.",
      },
    ],
  },
  {
    version: "0.43.2",
    date: "October 2026",
    features: [
      {
        heading: "Faster Large Heaps",
        description:
          "Very large heaps are much quicker to work with. Dragging the date " +
          "slider, switching the colour scheme and applying a cell filter now " +
          "respond straight away, and the heap no longer redraws in the " +
          "background while you are not interacting with it.",
      },
      {
        heading: "Heap Editor Fixes",
        description:
          "Deleted cells no longer show up as a stray block in the middle of " +
          "the heap, and painted cells disappear as soon as they are cleared.",
      },
    ],
  },
  {
    version: "0.43.1",
    date: "October 2026",
    features: [
      {
        heading: "Same-Day Stacking Periods",
        description:
          "A mine-schedule row whose start and end dates are the same day is " +
          "now stacked on that day. Previously those rows were left out of " +
          "the heap without a warning.",
      },
    ],
    note:
      "If a stacking plan you imported earlier has rows that start and end " +
      "on the same day, re-import it (or re-enter those rows) and rebuild " +
      "the heap to bring that ore in.",
  },
  {
    version: "0.43.0",
    date: "October 2026",
    features: [
      {
        heading: "Cells View for Leach Cycles",
        description:
          "The Leach Cycles step of the heap editor has a new Cells view that " +
          "colours every cell by how long it has been under leach, counting " +
          "the time solution reaches it from the cells stacked above.",
      },
      {
        heading: "Inspect a Single Cell",
        description:
          "Double-click a cell in the Cells view to see its days under leach " +
          "in the side panel, with a compact cross-section alongside the " +
          "coverage timeline.",
      },
    ],
  },
  {
    version: "0.42.3",
    date: "October 2026",
    features: [
      {
        heading: "Auto-Generated Snapshots for Rebuilt Heaps",
        description:
          "On the Run page, auto-generating snapshots now works for heaps " +
          "built with the stacking wizard or a server rebuild: the date range " +
          "fills in from the heap's leach schedule instead of staying empty.",
      },
      {
        heading: "Steadier Snapshot Dates",
        description:
          "Generated snapshot dates no longer shift by a day depending on your " +
          "time zone, and monthly steps keep to month ends (Jan 31, Feb 28, " +
          "Mar 31) instead of drifting into the next month.",
      },
      {
        heading: "Help Center Updates",
        description:
          "A new section explains scenario locking, and the chapters are now " +
          "numbered consecutively.",
      },
    ],
  },
  {
    version: "0.42.2",
    date: "October 2026",
    features: [
      {
        heading: "Only Admins Can Unlock Someone Else's Lock",
        description:
          "You can still lock a scenario and unlock your own locks as before. " +
          "A scenario locked by another user can now only be unlocked by an " +
          "admin — hover the greyed-out unlock button to see who locked it.",
      },
    ],
  },
  {
    version: "0.42.1",
    date: "October 2026",
    features: [
      {
        heading: "Colour Heaps by Bulk Density",
        description:
          "On the Heaps page, Color By now offers Bulk Density, so you can " +
          "see where ore is packed more or less densely. Cells that are only " +
          "partly filled are measured by the ore they actually hold.",
      },
      {
        heading: "Density Warnings",
        description:
          "Cells above 130 lb/ft³ are flagged as a warning and above " +
          "175 lb/ft³ as an elevated warning. A bar under the 3D view counts " +
          "them, View Density Warnings highlights them, and a flagged cell's " +
          "details say so too.",
      },
      {
        heading: "Export Floating-Block Errors",
        description:
          "When a heap has floating or out-of-order blocks, Export Errors " +
          "downloads an Excel list of every gap and stacking-date mismatch, " +
          "ready to fix in your mine plan.",
      },
      {
        heading: "Zoom Stacking Analytics and Compare",
        description:
          "Zooming the Stacking Analytics chart now sets its From and To " +
          "dates. In Results → Compare, zooming in shows every day of each " +
          "run rather than a thinned overview.",
      },
      {
        heading: "A More Polished Interface",
        description:
          "The Leach Cycles stage of the heap editor shows its date slider " +
          "and coverage timeline again, and Help & Documentation has new " +
          "sections and screenshots for the heap editor, build summary, QA " +
          "report and heap comparison.",
      },
    ],
  },
  {
    version: "0.42.0",
    date: "October 2026",
    features: [
      {
        heading: "Zoom Charts Across the App",
        description:
          "The zoom from Results now works on Operational Data, Climate, " +
          "Leaching flow rates, Closure, Reconciliation, As-Built, Kinetics, " +
          "Kinetics Fitting and Hydrodynamics. Drag across a chart or hold " +
          "Shift and scroll to zoom; double-click or Reset zoom to see " +
          "everything again.",
      },
      {
        heading: "Linked Panels Zoom Together",
        description:
          "Charts that share a timeline move as one: zoom any of the four " +
          "Leaching flow-rate panels, or any Closure chart, and the others " +
          "follow.",
      },
      {
        heading: "More Detail When Zoomed In",
        description:
          "Kinetics curves now include every day, and fitted and " +
          "hydrodynamic curves are drawn more finely, so zooming into early " +
          "days or the outflow onset shows real detail.",
      },
      {
        heading: "Learn More from Help Popups",
        description:
          "Every ? help popup now has a Learn more button that opens the " +
          "matching section of Help & Documentation in a new tab, jumping " +
          "straight to the relevant heading.",
      },
    ],
  },
  {
    version: "0.41.3",
    date: "October 2026",
    features: [
      {
        heading: "Shift + Scroll to Zoom Charts",
        description:
          "Zooming a Results chart with the scroll wheel now uses Shift + scroll, " +
          "so Ctrl + scroll is back to zooming the page as usual. Dragging across " +
          "the chart still zooms to that stretch of time.",
      },
      {
        heading: "Zoomed Charts Keep Zero",
        description:
          "When you zoom in, the vertical axis still starts at zero and only its " +
          "top adjusts to fit what you are looking at, so values keep their " +
          "sense of scale.",
      },
    ],
  },
  {
    version: "0.41.2",
    date: "October 2026",
    features: [
      {
        heading: "Zoom Into Results Charts",
        description:
          "Drag across a Results chart to zoom into that stretch of time, or " +
          "hold Ctrl and scroll (pinch on a trackpad) to zoom around the mouse. " +
          "The vertical axis rescales to fit what you are looking at; " +
          "double-click or Reset zoom to see the whole run again.",
      },
      {
        heading: "Every Day on the Chart",
        description:
          "Results charts now draw every simulated day rather than a thinned-out " +
          "sample, so short peaks and day-to-day steps show up — most clearly " +
          "when you zoom in. The dates along the bottom are evenly spaced and " +
          "no longer overlap at the end.",
      },
    ],
    note:
      "Daily charts on long runs may look busier than before: earlier versions " +
      "skipped days, which could hide short peaks.",
  },
  {
    version: "0.41.1",
    date: "October 2026",
    features: [
      {
        heading: "Compare Shows Runs of Different Lengths Correctly",
        description:
          "In Results → Compare, a run that ends sooner than another was drawn " +
          "stretched across the whole chart, so two runs that agree looked " +
          "different until you switched to Δ. Every run now lines up day for " +
          "day, and a shorter run simply stops where it ends.",
      },
      {
        heading: "Line Runs Up by Day or by Date",
        description:
          "A new Day N / Date switch on the Compare chart: compare runs from " +
          "their own start, or place them on one calendar so runs that start " +
          "on different dates line up. The chart tells you when the selected " +
          "runs start on different dates.",
      },
      {
        heading: "Pick the Units for a Compare Chart",
        description:
          "Compare charts open in your default units from Settings; a units " +
          "menu on the chart switches that metric to any other unit — Mt " +
          "instead of short tons, troy ounces instead of grams — without " +
          "changing your settings.",
      },
    ],
    note:
      "If you compared runs of different lengths in Raw view before this update, " +
      "it is worth a second look — the shorter run's curve was misplaced.",
  },
  {
    version: "0.41.0",
    date: "October 2026",
    features: [
      {
        heading: "Cadenza Can Fix a Mine Schedule or Block Model",
        description:
          "On the scenarios it creates, the AI assistant can now read and edit a " +
          "heap's mine schedule and block model — correct a grade or tonnage, " +
          "move blocks to the right group, add or remove periods — previewing " +
          "every change before it saves, then re-stack the heap.",
      },
      {
        heading: "Show Only What an Amend Changes",
        description:
          "When you amend a block model, click a colour under the 3D diff to " +
          "hide or show those blocks — for example to look at only the changed " +
          "and new ones.",
      },
      {
        heading: "Clearer Stacking Plan Warnings",
        description:
          "Warnings about scheduled tonnes that will not stack now name the " +
          "group, so they are not mistaken for period numbers.",
      },
    ],
    note:
      "The assistant can re-stack a heap at most 25 times an hour; re-stacking " +
      "from the heap editor is not limited.",
  },
  {
    version: "0.40.2",
    date: "October 2026",
    features: [
      {
        heading: "Review a Mine Schedule Amend Before It Applies",
        description:
          "Import / Amend on the Stacking Plan now shows how the file compares " +
          "with the schedule: new rows, rows that change tonnage or grades, and " +
          "rows already there. Choose what to do with each before anything " +
          "changes, and undo the whole amend in one step.",
      },
      {
        heading: "Re-Forecast From a Date",
        description:
          "Scheduled rows from the file's first date onwards that the file " +
          "leaves out can be kept or removed, so a forecast can replace the " +
          "rest of the plan in one import.",
      },
      {
        heading: "Choose How Periods Are Numbered",
        description:
          "Added rows can carry on from the last period (period 1 in the file " +
          "becomes the next period), keep the file's own period numbers, or be " +
          "left blank to fill in on the grid. The review shows each row's " +
          "period before you apply.",
      },
    ],
  },
  {
    version: "0.40.1",
    date: "October 2026",
    features: [
      {
        heading: "Amending a Mine Schedule Keeps Periods Apart",
        description:
          "Adding periods to a heap's mine schedule with Import → Amend now " +
          "numbers them after the periods already there, so new periods stack " +
          "in their own date ranges instead of merging into earlier periods " +
          "of the same group.",
      },
      {
        heading: "A Warning for Periods With Mixed Dates",
        description:
          "The Stacking Plan, and the preview before a rebuild, now warn you " +
          "when one period and group has rows with different dates, which " +
          "would be stacked together. A Year or Month column is also no longer " +
          "picked as the period when you import a schedule.",
      },
    ],
    note:
      "If you amended a schedule before this update and rebuilt the heap, check " +
      "the Stacking Plan for this warning, then rebuild.",
  },
  {
    version: "0.40.0",
    date: "October 2026",
    features: [
      {
        heading: "Review a Rebuild Before You Run It",
        description:
          "In the heap editor, Save & rebuild now offers Review changes: a " +
          "3D comparison of the heap you have and the heap the rebuild will " +
          "make, before anything is saved. Switch between Overlay, Side by " +
          "side and Swipe, just like Compare Heaps.",
      },
      {
        heading: "See Exactly Which Cells Change",
        description:
          "Every cell is marked as unchanged, new, removed, stacked on a " +
          "different day, or changed in mass, grade, ore type or group. A " +
          "table lists the cells that differ, a click shows any cell before " +
          "and after, and the full list exports to Excel.",
      },
      {
        heading: "Check Your Leach Cycles First",
        description:
          "Colour the heap by its leach cycles to see which cells keep " +
          "theirs, which are now stacked after their cycles start, which a " +
          "new lift buries, and which new cells still need cycles — with the " +
          "dates to generate them for.",
      },
      {
        heading: "Correct Dates on Compare Heaps",
        description:
          "Stacking dates on Compare Heaps no longer show one day early in " +
          "the Americas.",
      },
    ],
  },
  {
    version: "0.39.1",
    date: "October 2026",
    features: [
      {
        heading: "Pinch to Zoom on the 3D Heap Viewer",
        description:
          "On phones and tablets you can now pinch to zoom in and out of the " +
          "3D heap on the Heaps page, just as you can on the Leaching page. " +
          "It also works in the heap editor, Compare Heaps and the snapshot " +
          "viewer, and two fingers still pan the view.",
      },
    ],
  },
  {
    version: "0.39.0",
    date: "October 2026",
    features: [
      {
        heading: "Cadenza Can Generate and Trim Leach Cycles",
        description:
          "The AI assistant can now run the same Generate Leach Cycles tool " +
          "you use on the Leaching page, cut a date window out of a " +
          "schedule (for example a plant shut-down) and read a schedule back " +
          "to check it. It works only on scenarios it created, previews the " +
          "change before saving, and the previous schedule can always be " +
          "restored.",
      },
      {
        heading: "Cadenza Works With Your Lab Test Library",
        description:
          "Ask the assistant to load column or bottle-roll results into the " +
          "project's lab test library, tidy their details, and fit kinetics " +
          "straight from the library — just as the Kinetics Fitting page " +
          "does. A repeat of a fit you already ran points you to the " +
          "existing one instead of running it again.",
      },
      {
        heading: "Finer Kinetics Edits From Cadenza",
        description:
          "On the scenarios it creates, the assistant can now add, change, " +
          "re-date or remove a single extraction curve, or remove a metal's " +
          "kinetics, without rewriting every other date.",
      },
      {
        heading: "Leach Cycles Survive a Heap Re-stack",
        description:
          "Appending blocks or periods to an existing heap and re-stacking " +
          "it now keeps the leach cycles already on the heap, so you only " +
          "need to generate cycles for the new cells.",
      },
      {
        heading: "Safer Account Data",
        description:
          "Information about who imported a heap now shows only the " +
          "person's name and email, and account details are tightened " +
          "elsewhere too.",
      },
    ],
    note:
      "A re-stack keeps cycles for cells that didn't move. If blocks moved " +
      "or were removed, check the Leaching page before running a simulation.",
  },
  {
    version: "0.38.0",
    date: "October 2026",
    features: [
      {
        heading: "Generate Leach Cycles for Part of the Heap's Life",
        description:
          "Generate Leach Cycles now has a time scope. Pick a date window " +
          "and the generator only touches that period: cells stacked inside " +
          "it get new cycles and everything outside it is left exactly as it " +
          "was. Cells stacked earlier can keep their cycles or be restarted " +
          "on the window's first day — handy for planning a new year on top " +
          "of recorded actuals.",
      },
      {
        heading: "An Application Rate That Changes Over Time",
        description:
          "Set a starting rate and any number of dated changes instead of " +
          "one rate for the whole run. A cell whose cycle spans a change " +
          "keeps leaching at the new rate, and flow-rate matching uses the " +
          "rate in force on each day. The steps can be typed in or imported " +
          "from a spreadsheet.",
      },
      {
        heading: "See the Plan Before You Generate",
        description:
          "A timeline above the settings shows the heap's stacking span, " +
          "the window being regenerated and the rate steps, with a count of " +
          "cells inside, before and after the window. The warning spells out " +
          "which dates will be replaced, and the summary afterwards reports " +
          "what was regenerated, left untouched and clipped.",
      },
      {
        heading: "Flow Matching Respects What You Keep",
        description:
          "When a window keeps earlier cycles that are still running, their " +
          "flow counts toward the target, so matching only makes up the " +
          "difference.",
      },
    ],
    note:
      "Cells stacked before the window keep every cycle they already have, " +
      "including cycles that run into the window, unless you choose to " +
      "restart them. If a windowed result looks lighter than expected, check " +
      "the \"left untouched\" count in the summary.",
  },
  {
    version: "0.37.1",
    date: "October 2026",
    features: [
      {
        heading: "One Meaning for Every Unit",
        description:
          "A tonne is now a metric tonne and a metal ounce a troy ounce on " +
          "every page, chart, table and export. Previously a few pages read " +
          "the same unit setting as a short ton or an avoirdupois ounce, so " +
          "one heap could show totals that disagreed by about 10%.",
      },
      {
        heading: "Leach Schedules on Multi-Ore Heaps",
        description:
          "Heaps built with more than one ore type per cell no longer lose " +
          "leach intervals when a schedule is applied, and the heap layout " +
          "now counts each cell once.",
      },
      {
        heading: "Your Column Units in Excel Exports",
        description:
          "A unit you change for a column in the export wizard is now the " +
          "unit written to the workbook, even when the template has its own " +
          "unit system. Dates are written as real Excel dates, so they sort " +
          "and filter as dates.",
      },
      {
        heading: "Operational Data Keeps Its Meaning",
        description:
          "Operational data columns saved before this update keep the units " +
          "they were entered in, and the unit pickers now spell out short " +
          "tons and avoirdupois ounces explicitly.",
      },
    ],
    note:
      "Saved unit preferences and export templates were not changed: a " +
      "stored \"t\", metal \"oz\", \"oz/gal\" or \"lb/t\" now means metric " +
      "tonne, troy ounce, troy ounce per gallon and pounds per metric tonne. " +
      "Check your unit settings and templates if you relied on short tons.",
  },
  {
    version: "0.37.0",
    date: "September 2026",
    features: [
      {
        heading: "See How Each Heap Build Went",
        description:
          "Building a heap from a block model and mine schedule now ends " +
          "with a build summary: when stacking started and finished, how " +
          "much scheduled ore was never stacked, and how many blocks were " +
          "left unfilled, with a breakdown for every group.",
      },
      {
        heading: "The Summary Stays With the Heap",
        description:
          "Each heap card shows its stacking window, unstacked ore and " +
          "unfilled blocks. The same summary appears before you confirm a " +
          "rebuild, alongside every past rebuild in the heap's history, " +
          "and in a new Stacking section of the Heap QA report.",
      },
      {
        heading: "Mine Schedules With Named Periods",
        description:
          "Schedules whose periods are labelled with text such as " +
          "\"2027-01\" or \"P3\" are now ordered by their start dates, so " +
          "every period is stacked in its own time window.",
      },
      {
        heading: "A More Polished Interface",
        description:
          "The import wizard now stays open on its final step so you can " +
          "review the result, and the heap export menu no longer gets cut " +
          "off at the edge of the heap list.",
      },
    ],
    note:
      "If you built a heap from a mine schedule whose periods were labelled " +
      "with text rather than plain numbers, its ore may have been stacked " +
      "in too few periods. Rebuild it and check the new build summary.",
  },
  {
    version: "0.36.3",
    date: "September 2026",
    features: [
      {
        heading: "Compare Any Two Heaps Across Scenarios",
        description:
          "On the Compare Heaps page, choosing another scenario now lets " +
          "you pick any of its heaps, not only one with the same name as " +
          "the heap you started from. A heap with a matching name is still " +
          "selected for you, and you can switch to a different one.",
      },
    ],
  },
  {
    version: "0.36.2",
    date: "September 2026",
    features: [
      {
        heading: "Step Through Time While Comparing Heaps",
        description:
          "The Compare Heaps page now has the same date slider as the " +
          "Heaps page. Drag it to see both heaps as they stood on any day " +
          "of the stacking schedule, in Overlay, Side by side or Swipe " +
          "view, so you can spot where and when two plans diverge.",
      },
      {
        heading: "Both Heaps, Same Moment",
        description:
          "The slider covers the full span of both heaps, including cells " +
          "whose stacking date moved between them, and every view follows " +
          "the one date. The bar at the bottom shows how many cells of " +
          "each heap are in place on the chosen day.",
      },
    ],
    note:
      "The summary panel (tonnes, metal, changed cells) always describes " +
      "the whole heap, not just the cells shown at the selected date.",
  },
  {
    version: "0.36.1",
    date: "September 2026",
    features: [
      {
        heading: "Compare Runs Against Site Actuals",
        description:
          "The Compare window on the Results page now lists your " +
          "Operational Data logs alongside the runs. Tick a logged column " +
          "and it is drawn as a dashed line over any number of runs and " +
          "scenarios, so you can see at a glance which case tracks what " +
          "actually happened.",
      },
      {
        heading: "Lined Up By Date, In Your Units",
        description:
          "With actuals on the chart the axis switches to calendar dates, " +
          "runs from different scenarios line up by their own start dates, " +
          "and both sides are shown in your preferred units. Weekly or " +
          "irregular logs are handled fairly: running totals restart from " +
          "the first logged date and each entry counts as the total since " +
          "the one before.",
      },
      {
        heading: "How Far Off Is Each Run",
        description:
          "The difference views measure every run against the actuals, and " +
          "each run gets a badge with its cumulative variance and average " +
          "error — green when within ten percent. The full statistics stay " +
          "on the Reconciliation page.",
      },
      {
        heading: "One Mapping, Shared Everywhere",
        description:
          "A log column already matched to a results column on the " +
          "Reconciliation page jumps straight to that metric. A new column " +
          "asks you to pick its results column once; the choice is saved on " +
          "the log and reused by Reconciliation and by everyone on the " +
          "project.",
      },
    ],
    note:
      "If a log column and a results column measure different kinds of " +
      "quantity, the chart compares the stored values as they are and says " +
      "so under the chart — check the mapping before reading the numbers.",
  },
  {
    version: "0.36.0",
    date: "September 2026",
    features: [
      {
        heading: "Block Model Statistics",
        description:
          "The Block Model stage gains a Stats layout: headline tonnes and " +
          "grades, grade-tonnage curves per group and ore type, an " +
          "attribute histogram, tonnes by lift and a cutoff table. Select " +
          "every block below a cutoff in one click, and export the whole " +
          "set to Excel.",
      },
      {
        heading: "Reshape The Heap Geometry",
        description:
          "A Geometry group on the Plan layout adds a lift above the " +
          "selected columns, extends the pad by a polygon you draw, or " +
          "trims blocks outside or inside a polygon or above an " +
          "elevation. Edits preview on the plan view before you apply " +
          "them, and undo and Save work as they always have.",
      },
      {
        heading: "Leach Coverage",
        description:
          "The Leach Cycles stage gains a Coverage view: a timeline of " +
          "stacks placed against stacks under leach, the heap coloured by " +
          "days under leach, one-click selection of cells that were never " +
          "leached or were buried before their first cycle, and a " +
          "per-period coverage table you can download.",
      },
      {
        heading: "Mine Plan vs As-Built",
        description:
          "A new Reconciliation page compares the saved mine schedule with " +
          "surveyed tonnes from an Operational Data log, period by period " +
          "and cumulatively, and optionally an as-built surface against " +
          "the stacked top. Export it to Excel, or apply the actual " +
          "tonnes straight to the stacking plan.",
      },
      {
        heading: "Rebuild Large Heaps On The Server",
        description:
          "Save & rebuild now asks where to run. Large heaps default to a " +
          "server rebuild that runs in the cloud while you carry on " +
          "working: the heap card shows progress with a Cancel button, " +
          "the bell tells you when it finishes, and the Stacking Plan " +
          "panel keeps a history of rebuilds.",
      },
    ],
    note:
      "A server rebuild works from the last saved block model and " +
      "schedule, so both are saved before it starts.",
  },
  {
    version: "0.35.1",
    date: "September 2026",
    features: [
      {
        heading: "Heap Tools Now Live",
        description:
          "The 0.35.0 release did not reach the web app because of a build " +
          "problem in the Compare heaps page styling. This release fixes " +
          "it, so the plan-view painting, Heap QA report, schedule shaping " +
          "and Compare heaps tools described below are now available.",
      },
    ],
  },
  {
    version: "0.35.0",
    date: "September 2026",
    features: [
      {
        heading: "Paint Groups Onto The Block Model",
        description:
          "The Block Model stage gains a Plan layout: a top-down view of the " +
          "heap where you lasso, box or brush columns of blocks and assign " +
          "them to an existing or new ore-body group. Work one lift at a " +
          "time or whole columns, see the tonnes and current groups of your " +
          "selection, and get a warning when a group has no schedule yet.",
      },
      {
        heading: "Heap QA Report",
        description:
          "QA report… on a heap card's Edit menu checks the stacked heap and " +
          "its inputs in one go: floating and late cells, grid regularity, " +
          "groups scheduled but missing from the block model, unresolved " +
          "ore and hydro codes, and leach coverage, each with a link to the " +
          "tool that fixes it. Upload the planner's totals to reconcile " +
          "tonnes and metal per group, mark the heap reviewed, and export " +
          "the report to Excel.",
      },
      {
        heading: "Shape A Mine Schedule In One Move",
        description:
          "Slip dates, scale tonnes, split or merge periods, or generate a " +
          "schedule from a stacking rate. Each tool previews the change " +
          "before it is applied, in the overview chart and in the grid.",
      },
      {
        heading: "Compare Two Heaps",
        description:
          "Compare… opens a heap beside its previous version, another heap " +
          "in the scenario, or the same heap in another scenario. Cells are " +
          "coloured by what changed, with overlay, side-by-side and swipe " +
          "views, a summary of the differences, tonnes by group, a CSV of " +
          "changed cells, and a one-click restore of the previous version.",
      },
      {
        heading: "Solution Outlets On Heaps",
        description:
          "Stacks can be assigned to named solution outlets from the same " +
          "Plan layout. Outlets show in the heap viewer's colouring, filter " +
          "and cell panel; routing them through the water balance comes " +
          "in a later release.",
      },
    ],
    note:
      "The QA report's reconciliation compares against totals you upload, " +
      "so check the group names in that file match the block model's groups.",
  },
  {
    version: "0.34.0",
    date: "September 2026",
    features: [
      {
        heading: "Edit A Heap's Block Model And Mine Schedule",
        description:
          "The pencil on a heap card now opens an Edit menu: rename the heap, " +
          "or open its block model, mine schedule, heap edit or leach cycles " +
          "directly. The heap card also shows when each input was last saved " +
          "and whether the stacked heap is out of date.",
      },
      {
        heading: "A Block Table Beside The 3D View",
        description:
          "The Block Model stage lists every block next to the 3D model. " +
          "Select blocks in either view, change lift, sequence, group, fill, " +
          "ore, hydro, density and any extra attribute in place or across a " +
          "whole selection, add or remove blocks, and undo. Geometry changes " +
          "still go through the import and amend wizard.",
      },
      {
        heading: "Mine Schedule Overview And Capacity Check",
        description:
          "The Stacking Plan stage charts scheduled tonnes by quarter for each " +
          "group and checks them against what the block model can hold, " +
          "flagging any scheduled group that has no blocks. The schedule grid " +
          "gains named ore and hydro types, a date picker, unit choices, " +
          "duplicate and fill-down, paste from Excel, and undo.",
      },
      {
        heading: "Save Now, Rebuild When Ready",
        description:
          "Save a block model or mine schedule without re-stacking the heap. " +
          "Save & rebuild previews the result first: cells, tonnes and metal " +
          "placed before and after, plus any warnings.",
      },
      {
        heading: "Your Previous Version Is Kept",
        description:
          "Saving a block model or mine schedule keeps the version it " +
          "replaced. Restore previous, on the Block Model summary or the " +
          "Stacking Plan panel, swaps it back.",
      },
      {
        heading: "Block Models For Older Heaps",
        description:
          "A heap imported as a single combined file can now derive a block " +
          "model and mine schedule from its stacked cells, or import them, " +
          "so it can be edited and rebuilt like any other heap.",
      },
    ],
    note:
      "A saved block model or schedule does not change simulation results " +
      "until the heap is rebuilt. The heap card and the editor tell you when " +
      "the stacked heap is out of date.",
  },
  {
    version: "0.33.2",
    date: "September 2026",
    features: [
      {
        heading: "Phone Sign-In Completes First Time",
        description:
          "Signing in from a shortcut on the home screen of your phone no " +
          "longer stops at a Sign-in problem screen that needed a second " +
          "try. It now completes on the first attempt.",
      },
    ],
  },
  {
    version: "0.33.1",
    date: "September 2026",
    features: [
      {
        heading: "Sign-In That Works On Your Phone",
        description:
          "Signing in on a phone or tablet now takes you to the secure " +
          "sign-in page and straight back into Orecasting in the same tab, " +
          "instead of opening a second tab that could lose your place while " +
          "you typed your password. Shortcuts saved to your home screen sign " +
          "in the same way.",
      },
      {
        heading: "Your Name, Photo And Menu Always Load",
        description:
          "Orecasting no longer opens with a blank profile or a thinned-out " +
          "project menu after signing in. If your details cannot be loaded " +
          "you are returned to the sign-in page rather than into a " +
          "half-loaded app.",
      },
    ],
    note:
      "If sign-in on a phone still stalls after this update, please tell us " +
      "which browser you used through Help > Contact.",
  },
  {
    version: "0.33.0",
    date: "September 2026",
    features: [
      {
        heading: "Groups That Never Come Out As A Single Test",
        description:
          "Kinetics Fitting now keeps every group to a minimum size (three " +
          "tests unless you say otherwise), absorbs undersized groups into " +
          "their nearest neighbour, and sets aside any test that will not " +
          "group with anything as an outlier with a reason. A new " +
          "separation chart shows how many groups your program really " +
          "supports, and a Single test badge flags any group that is just " +
          "one column's own fit.",
      },
      {
        heading: "More Ways To Group A Program",
        description:
          "Group by ore type and then by similarity within each type, let " +
          "same-day batches merge freely on the time basis or widen it to a " +
          "date window, compare curve shape only so the ultimate does not " +
          "drive the grouping, and choose Ward linkage for more balanced " +
          "groups. Adjusting groups by hand now moves a test into any " +
          "group, a new group, or out of the fit, and can merge a group " +
          "with its nearest neighbour.",
      },
      {
        heading: "Sharper Fits, With Honest Uncertainty",
        description:
          "Every fit is polished to the true optimum after the search, so " +
          "confidence intervals are right. Group and overall curves carry " +
          "a shaded prediction band. A test that ended well before its " +
          "fitted ultimate is flagged as extrapolated. Weighting options " +
          "let each test count equally in a pooled group and stop late " +
          "points from dominating. Two new curve families, Avrami and " +
          "shrinking core, join the model set.",
      },
      {
        heading: "Kinetics That Remember Where They Came From",
        description:
          "Applying a fit to an ore property now records which fit, which " +
          "curve and which parameters produced the table. The Kinetics " +
          "page shows From fit with a link straight back to it, whether the " +
          "fit was applied from the page or through the assistant.",
      },
      {
        heading: "A Fuller Lab Test Record",
        description:
          "Lab tests carry head and residue grades with a metallurgical " +
          "balance check against the curve, test conditions, solution " +
          "chemistry and a QA flag. The library shows duration, whether a " +
          "test reached a plateau, and warns when two tests share the same " +
          "series. Sort any column, page through large libraries, and " +
          "tag, edit or delete many tests at once.",
      },
    ],
    note:
      "Grouping defaults changed: a requested number of groups is now a " +
      "target, and the applied count is shown with the result. Re-running " +
      "an older grouped fit can produce fewer, larger groups.",
  },
  {
    version: "0.32.0",
    date: "September 2026",
    features: [
      {
        heading: "A Lab Test Library For Every Project",
        description:
          "The new Lab Tests page keeps a project's column and bottle-roll " +
          "tests in one place — organised by ore type, metal, test type, " +
          "sample date and your own tags rather than folders, so a program " +
          "of several hundred tests stays searchable. Import a whole " +
          "program from a pasted table or a workbook, edit a test once, and " +
          "pick any selection of tests straight into a fit.",
      },
      {
        heading: "Fit Up To A Thousand Tests At Once",
        description:
          "Kinetics Fitting now takes up to 1,000 tests in one run instead " +
          "of 24, and tells you before you start roughly how long the fit " +
          "will take — including the minute or so the compute container " +
          "needs to start. Very large programs are stored in full and " +
          "reopen exactly as they were run.",
      },
      {
        heading: "Name, Tag And Find Your Fits",
        description:
          "Give a fit a name, a description and tags before you run it. " +
          "Recent Fits shows who ran each one, can be searched, narrowed to " +
          "your own fits or to a tag, and expands to the full history. " +
          "Running a fit identical to one already in the scenario offers to " +
          "open the existing result instead of computing it again.",
      },
      {
        heading: "Group Tests By Ore Type",
        description:
          "Grouped fitting gains a third basis: every test carrying the same " +
          "ore type is pooled into one curve, labelled with that ore type, " +
          "alongside grouping by sample date or by curve similarity. Tests " +
          "brought in from the library carry their ore type with them.",
      },
      {
        heading: "Kinetics Tools Has Its Own Place In The Sidebar",
        description:
          "Lab Tests and Kinetics Fitting have moved out of Heap Leach into " +
          "a Kinetics Tools group of their own, since they help develop the " +
          "kinetics rather than describe the heap. Administrators can show " +
          "or hide the group and its pages per project as before.",
      },
      {
        heading: "A Tidier Fitting Workspace",
        description:
          "One Add tests menu replaces five toolbar buttons; tests taken " +
          "from the library show a Library badge with a one-click reset " +
          "after edits, and a pasted test can be saved into the library; " +
          "reopening an older fit warns when its library tests have changed " +
          "since; results from large runs page in groups; and the Recent " +
          "Fits list no longer squeezes its buttons past the edge of the " +
          "card.",
      },
    ],
    note:
      "A fit copies its tests at the moment it runs, so editing or " +
      "deleting a library test later never changes an earlier fit. Each " +
      "person may keep up to 1,000 fits per project; delete old ones from " +
      "Recent Fits when you reach the limit.",
  },
  {
    version: "0.31.0",
    date: "September 2026",
    features: [
      {
        heading: "Pictures Of Your Fit In The Excel Export",
        description:
          "The kinetics fit workbook now opens with a Charts sheet: every " +
          "test's points with the fitted curves, a chart for each group " +
          "showing only its members and their pooled curve, and one chart " +
          "per test. The Curves and Lab Data sheets still hold the numbers " +
          "behind them if you would rather build your own chart.",
      },
      {
        heading: "See When A Heap Was Created And By Whom",
        description:
          "Selecting a heap on the Heaps page now shows when it was " +
          "created, who added it, and when it was last changed — useful " +
          "when a scenario has several heaps and you need to know which " +
          "one is the newest.",
      },
      {
        heading: "Requesting A Demo Now Reaches Us",
        description:
          "The Request a Demo form on the landing page used to hand off to " +
          "your mail application, which quietly did nothing if you did not " +
          "have one set up. It now sends the request directly and confirms " +
          "that it arrived.",
      },
      {
        heading: "Email Alerts For Incoming Requests",
        description:
          "Administrators can choose which kinds of request — help, bug, " +
          "feature or demo — they want to be emailed about, and the " +
          "review screen now shows the full contact details that came with " +
          "a demo request.",
      },
    ],
  },
  {
    version: "0.30.2",
    date: "September 2026",
    features: [
      {
        heading: "Export A Kinetics Fit To Excel",
        description:
          "Export to Excel, beside Apply to Kinetics, downloads the fit as a " +
          "workbook: the settings and warnings, every test with the group it " +
          "was assigned to and its fitted parameters, the groups with their " +
          "members, date ranges and pooled fits, every candidate model, the " +
          "size trend and overall equation, the predicted curves by day, and " +
          "the lab points the fit used.",
      },
      {
        heading: "See The Points Behind Each Group Curve",
        description:
          "When a fit has groups, a Show row above the chart narrows it to " +
          "one group — just its member tests' points and its pooled curve — " +
          "or to the tests that were left out of every group.",
      },
      {
        heading: "Missing Assays Are Skipped, Not Read As Zero",
        description:
          "A blank lab cell, or one marked NA, ND, a dash or the like, now " +
          "means the test has no sample that day. Pasting no longer raises " +
          "an error for each one, the preview says how many were skipped, " +
          "and an empty cell in the Test Data grid is no longer plotted or " +
          "fitted as 0%.",
      },
    ],
    note:
      "If an earlier fit included a test with a blank extraction cell, that " +
      "cell counted as 0% — rerun the fit to drop it.",
  },
  {
    version: "0.30.1",
    date: "September 2026",
    features: [
      {
        heading: "Group Kinetics Tests Without Sample Dates",
        description:
          "Kinetics Fitting can now group tests with similar kinetics even " +
          "when none of them carries a sample date. Set Group by to curve " +
          "similarity and any set of tests — composites, variability " +
          "samples, an undated column program — is sorted into groups of " +
          "matching curves, with the same tolerance and number-of-groups " +
          "controls. It is chosen for you whenever fewer than two tests are " +
          "dated; grouping over time by sample date works exactly as before.",
      },
      {
        heading: "Adjust Groups On Either Basis",
        description:
          "Move a test between groups and refit whether the groups were " +
          "formed by date or by similarity. A group with no dated members " +
          "simply shows no date range, and applying one curve per group " +
          "still lets you set each effective date.",
      },
    ],
  },
  {
    version: "0.30.0",
    date: "September 2026",
    features: [
      {
        heading: "Import From Several Sheets At Once",
        description:
          "Every spreadsheet import — heaps, kinetics, flow rates, mine " +
          "schedules, climate, plant schedules, operational logs — now lists " +
          "the sheets in a workbook. Switch any of them on and its columns " +
          "join the pickers, grouped by sheet, so a block model, its " +
          "schedule and its assays no longer have to be pasted into one tab.",
      },
      {
        heading: "Point At The Table, Not The Sheet",
        description:
          "A Data region panel shows the sheet as a grid. Drag or type a " +
          "range to import just that table — below a title block, beside a " +
          "notes column, or a few columns out of a wide sheet. A units row " +
          "under the header folds into the field names, and a trailing " +
          "TOTAL row is left out.",
      },
      {
        heading: "Sideways And Matrix Tables",
        description:
          "Monthly summaries with the months across the top, and planners' " +
          "tonnes-by-period grids, can be read as they are: choose Rows or " +
          "Matrix in the region panel and the wizard turns them into the " +
          "one-row-per-record shape it expects, with the period labels " +
          "understood as dates.",
      },
      {
        heading: "Match Rows Across Sheets",
        description:
          "With more than one sheet in use, rows are matched on a key " +
          "column — a block ID, a date, a group code, or several together — " +
          "or by row order. A live count shows how many rows matched before " +
          "anything is imported.",
      },
      {
        heading: "Mine Schedule Columns Become Attributes",
        description:
          "Extra columns in a mine schedule, such as the pit or phase the " +
          "ore came from, can be attached as attributes on every block that " +
          "row fills, and appear in the heap viewer's Color By, cell filter " +
          "and cell panel alongside block-model attributes.",
      },
      {
        heading: "Choose Which Tests Go Into A Kinetics Fit",
        description:
          "The Test Data editor lets you tick tests in or out of a fit, " +
          "filter them by label or P80, and cut a series off after a chosen " +
          "leach day. Long day-by-day programs plot faster.",
      },
    ],
    note:
      "Files with one sheet import exactly as before. Where sheets are " +
      "matched on a key, check the match counts: an unmatched row is " +
      "imported with blanks from the other sheet, not dropped.",
  },
  {
    version: "0.29.1",
    date: "September 2026",
    features: [
      {
        heading: "Pick Your Units In The Hydro Property Form",
        description:
          "Every physical field in the Add / Edit Hydro Property dialog now " +
          "has a unit picker beside it — hydraulic conductivity, the " +
          "moisture contents, the flowing-fraction settings, the mass " +
          "transfer coefficient, pore diffusivity, precipitation " +
          "infiltration and the depth-recovery depths. Switching the unit " +
          "converts the number in place, so nothing needs re-typing.",
      },
      {
        heading: "Units Follow Your Preferences",
        description:
          "Each picker starts from your unit settings — percent or fraction " +
          "for ratios, metres or feet for depths, inches for particle " +
          "diameter when you work in imperial — and any of them can be " +
          "overridden for a single entry.",
      },
    ],
    note:
      "Values are stored exactly as before, so existing hydro properties " +
      "and simulation results are unaffected.",
  },
  {
    version: "0.29.0",
    date: "September 2026",
    features: [
      {
        heading: "Bring Your Own Columns",
        description:
          "When importing a heap, any extra column in your file — an assay, " +
          "a rock code, a zone, a mining phase — can now be attached to the " +
          "heap as a named attribute. Add them one at a time or pull in " +
          "every unmapped column with a single click.",
      },
      {
        heading: "Colour By Anything You Imported",
        description:
          "Every attribute you add shows up in the heap viewer's Color By " +
          "list: numbers get a graded colour scale, categories get one " +
          "colour per value with a matching legend.",
      },
      {
        heading: "Filter On Your Attributes",
        description:
          "The cell filter offers a min/max rule for numeric attributes and " +
          "a pick-list for categories, so you can isolate, say, every " +
          "oxide-zone cell above a copper cut-off.",
      },
      {
        heading: "See Them On Every Cell",
        description:
          "Double-click a cell and its attributes appear alongside the " +
          "mine-plan details. Mine-plan period, sequence, lift and group " +
          "have also joined the Color By list.",
      },
    ],
    note:
      "Attributes are captured at import time. Heaps imported before this " +
      "update carry none until they are re-imported or rebuilt, and blank " +
      "cells in your file show as “not recorded”.",
  },
  {
    version: "0.28.0",
    date: "September 2026",
    features: [
      {
        heading: "Filter The Cells You See",
        description:
          "The Heaps page 3D viewer has a new filter button beside Color By. " +
          "Stack rules on elevation, cell ore mass, bulk density, metal " +
          "grade, stacking date, or ore and hydro property, and only the " +
          "cells that match stay on screen.",
      },
      {
        heading: "Find Ore By Mass",
        description:
          "Pick one or more ore properties and ask for cells that hold at " +
          "least — or at most — a given mass of them, counted for any of " +
          "the selected ores, each of them, or all of them combined.",
      },
      {
        heading: "Mine-Plan Rules",
        description:
          "Heaps built from a block model and mine schedule can also be " +
          "filtered by period, stacking sequence, lift, stacker direction, " +
          "fill and group — for example every cell with a SEQ between 3 " +
          "and 50.",
      },
      {
        heading: "Hide Or Fade",
        description:
          "Choose whether the cells that do not match disappear entirely " +
          "or fade into a translucent outline of the heap, with an " +
          "opacity slider, so you can see exactly where the matching " +
          "cells sit. The same filter is available on the Run Simulation " +
          "page viewer.",
      },
    ],
    note:
      "Mine-plan rules only appear for heaps that carry those details. " +
      "Rebuild an older heap from its block model and mine schedule to " +
      "filter by sequence, lift or group.",
  },
  {
    version: "0.27.3",
    date: "September 2026",
    features: [
      {
        heading: "More Detail On Every Cell",
        description:
          "Double-click a cell in the Heaps page 3D viewer and the details " +
          "panel now includes a Mine Plan section: the schedule period the " +
          "cell was stacked in, its sequence and lift, the stacker " +
          "direction, the fill fraction when your block model supplied " +
          "one, and the mine-plan group it belongs to.",
      },
    ],
    note:
      "Heaps imported before this update do not carry these details yet. " +
      "Rebuild a heap from its block model and mine schedule to see them.",
  },
  {
    version: "0.27.2",
    date: "September 2026",
    features: [
      {
        heading: "Reuse A Curve You Already Have",
        description:
          "Kinetics Fitting can now start from a curve that is already in " +
          "your scenario. Pick the curves you are looking at on the " +
          "Kinetics page, send them across, and the fitting grid fills in " +
          "with their points — so you can re-derive the parameters behind " +
          "an extraction table without hunting down the original lab " +
          "sheet. You choose how densely each curve is sampled.",
      },
      {
        heading: "A Far Better Python Editor",
        description:
          "Custom model steps written in Python are now colour-coded the " +
          "way a code editor would show them, in both the pipeline and code " +
          "views. The editing panel grows to fill the width of your screen " +
          "instead of sitting in a narrow column, and any step can be " +
          "opened in a full-size editor window.",
      },
      {
        heading: "See What Your Step Can Use",
        description:
          "Each Python step now lists the values available to it — the " +
          "day's climate, the cell and ore state, plant or transfer " +
          "figures, whichever apply where you are editing. Click one to " +
          "drop it into your code, or start typing inside the brackets and " +
          "pick from the list.",
      },
      {
        heading: "A More Polished Interface",
        description:
          "Suggestion lists in the code editors no longer get cut off at " +
          "the edge of a short editor, and scroll when the list is long.",
      },
    ],
  },
  {
    version: "0.27.1",
    date: "September 2026",
    features: [
      {
        heading: "New Projects Start Simpler",
        description:
          "A newly created project now opens with just the everyday " +
          "heap-leach workflow in the sidebar — Heaps through Results, " +
          "Climate and Water Balance, and the task board. The specialist " +
          "modules (Geode Models, Analysis & Economics, Compliance and " +
          "Operation) start hidden, and an administrator ticks on the ones " +
          "the project actually needs from the project's edit dialog. " +
          "Existing projects are untouched.",
      },
      {
        heading: "Geode Models Has Its Own Switch",
        description:
          "Geode Models used to appear or disappear along with Run " +
          "Simulation. It now has its own entry in the project's section " +
          "list, so a project can run simulations normally while custom " +
          "models stay put away.",
      },
    ],
    note:
      "If a page you expect is missing from a project created after this " +
      "update, nothing has been lost — ask an administrator to turn the " +
      "section on in the project's edit dialog.",
  },
  {
    version: "0.27.0",
    date: "August 2026",
    features: [
      {
        heading: "Geode: Build Your Own Simulation Model",
        description:
          "A new simulation model whose math you can customize. Start from " +
          "a built-in template that matches the standard engine, then swap " +
          "in your own extraction kinetics, drainage behavior, plant " +
          "recovery, per-facility rules, or entirely new tracked outputs — " +
          "all through a visual pipeline editor or a purpose-built formula " +
          "language with live checking. Ask your administrator to enable " +
          "Geode access for your account.",
      },
      {
        heading: "See the Whole Day Loop",
        description:
          "The Geode editor's pipeline view lays out every step the " +
          "simulator runs each day — including your water balance circuit " +
          "— and the code view's Full Pipeline mode annotates each step " +
          "with what it reads, what it produces, and what it hands to the " +
          "next step.",
      },
      {
        heading: "Group Column Tests Over Time",
        description:
          "Kinetics Fitting can now group monthly or over-time column " +
          "tests by sample date and fit one pooled curve per group — " +
          "automatically by similarity or with manual assignments — and " +
          "apply each group as its own effective date in a single step.",
      },
      {
        heading: "A Smarter Assistant",
        description:
          "Connected AI assistants can now manage Geode model definitions " +
          "(with the same access rules as the editor), validate model code " +
          "before saving, and apply completed kinetics fits to ore " +
          "properties exactly like the Apply button.",
      },
      {
        heading: "Help That Keeps Up",
        description:
          "A full Geode chapter and a model-language reference join the " +
          "Help Center, alongside updated kinetics fitting documentation.",
      },
    ],
    note:
      "Geode model results depend on the formulas you write — the " +
      "simulator enforces mass balance and flags errors loudly, but " +
      "always sanity-check a custom model against a standard engine run " +
      "before using its numbers.",
  },
  {
    version: "0.26.2",
    date: "August 2026",
    features: [
      {
        heading: "Paste Lab Data as Percent or Decimal",
        description:
          "The Kinetics Fitting page now reads pasted and imported " +
          "extraction values either as percentages (26.8) or as decimal " +
          "fractions (0.268) — pick the format your lab sheets use and it " +
          "is remembered for next time.",
      },
      {
        heading: "Tidy Up Your Fit History",
        description:
          "Old or unwanted entries in the Recent Fits list can now be " +
          "deleted. Kinetics you have already applied to an ore property " +
          "are never affected — only the stored fit is removed.",
      },
    ],
  },
  {
    version: "0.26.1",
    date: "August 2026",
    features: [
      {
        heading: "Answers Where You Need Them",
        description:
          "Small “?” buttons now sit beside the trickier sections " +
          "of the NPV & Cashflow page, Ensembles, the pond and plant " +
          "editors, and the kinetics Tune panel — each opens a " +
          "plain-language explanation of what the settings do and how they " +
          "affect your results.",
      },
      {
        heading: "Particle Diameter on Hydraulic Properties",
        description:
          "Hydraulic properties can now record the ore's particle or " +
          "agglomerate diameter, entered in your preferred length unit. " +
          "Related mass-transfer settings are kept in step for you " +
          "automatically.",
      },
    ],
  },
  {
    version: "0.26.0",
    date: "August 2026",
    features: [
      {
        heading: "A Code View for Water-Balance Routing",
        description:
          "The Water Balance page gains a Code view: your entire routing — " +
          "every split, conditional rule, and overflow redirect — shown as " +
          "readable text. Review it at a glance, edit or paste changes, and " +
          "apply them back to the canvas as a single undoable step.",
      },
      {
        heading: "Move Circuits Between Scenarios",
        description:
          "Export the routing, or the whole water-balance network, to a " +
          "text file and import it into another scenario. Every value " +
          "carries its own unit, so a file written under one set of unit " +
          "preferences imports correctly under any other.",
      },
      {
        heading: "A Real Editor, Not Just a Text Box",
        description:
          "The code view highlights the routing language, numbers every " +
          "line, and checks your text as you type — problem lines are " +
          "marked in the margin, and hovering one shows exactly what is " +
          "wrong. Suggestions for node names, properties, and units appear " +
          "as you type.",
      },
      {
        heading: "Routing Language Documentation",
        description:
          "The Help Center's Water Balance chapter now covers the code " +
          "view, and a new Reference appendix documents the full routing " +
          "language — including worked examples for conditional routing " +
          "and overflow redirects.",
      },
    ],
    note:
      "Applying full-network code that leaves out an existing pond or " +
      "plant removes that node, and saving then deletes its stored " +
      "settings. The code view lists the affected nodes and asks for " +
      "confirmation before any such change is applied.",
  },
  {
    version: "0.25.0",
    date: "August 2026",
    features: [
      {
        heading: "A Redesigned Kinetics Fitting Workspace",
        description:
          "Each column test now lives on its own tab with an editable " +
          "day-by-day grid. Import the lab spreadsheet directly (.xlsx or " +
          ".csv), paste a whole multi-size program at once, or type values " +
          "in — and strike any suspect sample out of the fit with one " +
          "click, without deleting it.",
      },
      {
        heading: "The Partial Series Curve Model",
        description:
          "A new candidate model fits column data as three overlapping " +
          "extractions — a fast surface leach, a medium component, and a " +
          "slow tail. It is the same model the classic column-test " +
          "spreadsheets use, and it follows the early days of a test far " +
          "more faithfully than a single curve.",
      },
      {
        heading: "Pin the Ultimate, Fit the Shape",
        description:
          "The fit can now hold the ultimate recovery fixed — at the " +
          "highest value observed or at a value you choose — and fit only " +
          "the shape of the curve, the way column tests are traditionally " +
          "worked up. A new robust option also keeps one bad assay day " +
          "from bending the whole curve.",
      },
      {
        heading: "Tune a Fit Before Applying It",
        description:
          "A new Tune panel adjusts any fitted curve by hand — above all " +
          "the ultimate recovery — with the tuned curve drawn live on the " +
          "chart and its quality readouts updating as you drag. The tuned " +
          "curve can then be applied to an ore property exactly like a " +
          "fitted one.",
      },
      {
        heading: "Fit History",
        description:
          "Recent fits for the scenario are now listed on the page, and " +
          "any of them can be reopened with its data, settings and " +
          "results intact — a fit no longer disappears on a page refresh.",
      },
    ],
    note:
      "Applied fits now record the curve's full ultimate recovery rather " +
      "than the total reached by the end of the extraction table, so " +
      "slow-leaching curves store a slightly higher — and more accurate " +
      "— ultimate than they did before.",
  },
  {
    version: "0.24.3",
    date: "August 2026",
    features: [
      {
        heading: "Import A Flow Rate Plan",
        description:
          "Flow-rate matching in Generate Leach Cycles no longer has to be " +
          "typed in row by row. Import from file\u2026 reads a start date, end " +
          "date and target flow from a CSV or Excel plan, in whichever flow " +
          "unit your file uses. The rows land in the table ready to edit, and " +
          "nothing is written to the heap until you run the generator.",
      },
      {
        heading: "A Readable Generate Leach Cycles Form",
        description:
          "The Generate Leach Cycles dialog no longer runs off its own edge. " +
          "Unit dropdowns were taking up whole rows and squeezing the value " +
          "beside them down to a sliver; the dialog is also wider now, so all " +
          "four passes read comfortably, and it folds into a single labelled " +
          "column on a narrow window or a phone.",
      },
    ],
  },
  {
    version: "0.24.2",
    date: "August 2026",
    features: [
      {
        heading: "Typing In Dialogs Stays Put",
        description:
          "Text boxes inside pop-up dialogs no longer lose their place while " +
          "you type. Previously the cursor could jump out of the field after " +
          "each character, so you had to click back in to keep going.",
      },
      {
        heading: "Clearer Project Creation",
        description:
          "Creating a project now shows only the companies you are actually " +
          "allowed to create one in, and tells you which permission to ask " +
          "for if none of them are. Company administrators can grant the new " +
          "“Create Projects” permission to delegate this.",
      },
    ],
  },
  {
    version: "0.24.1",
    date: "August 2026",
    features: [
      {
        heading: "Terms Of Use And Attributions",
        description:
          "A new Terms of Use page is linked from the sign-in screen. It sets " +
          "out how Orecasting results should and should not be relied upon, " +
          "and credits the open-source software and mapping data the platform " +
          "is built on.",
      },
      {
        heading: "Climate Fetch Temporarily Limited",
        description:
          "Fetching climate data directly from the weather archive is " +
          "temporarily available to administrators only while we complete a " +
          "commercial data subscription. Importing climate from a CSV or " +
          "Excel file is unchanged and remains available to everyone.",
      },
      {
        heading: "A Lighter Install",
        description:
          "Unused libraries have been removed across the platform, trimming " +
          "what has to be downloaded and kept up to date.",
      },
    ],
    note:
      "If you normally fetch climate from the archive and no longer see that " +
      "option, upload a climate file instead or ask an administrator to fetch " +
      "it for you. Existing climate data on your scenarios is untouched.",
  },
  {
    version: "0.24.0",
    date: "August 2026",
    features: [
      {
        heading: "3D Print Your Heap",
        description:
          "The Heap Modeling page can now export a heap as an STL file ready " +
          "for a 3D printer. Pick the printer icon on a heap card, choose how " +
          "large the miniature should be, and preview exactly what will print " +
          "from every angle before you download it.",
      },
      {
        heading: "A Display Stand With Your Project's Name",
        description:
          "Add an optional stand under the printed heap — a round base and a " +
          "stem, each sized with sliders. Text of your choice, starting with " +
          "the project name, is embossed around the side of the base. Because " +
          "real heaps are much wider than they are tall, a vertical " +
          "exaggeration slider can thicken the profile so the miniature reads " +
          "well in hand.",
      },
      {
        heading: "Dialogs That Fit Their Content",
        description:
          "Dialogs across the app now come in four consistent sizes instead " +
          "of dozens of slightly different ones, so the same kind of task " +
          "always gets the same kind of window. On phones, the buttons at the " +
          "bottom of several dialogs no longer squash together — they stack " +
          "full-width where space is tight.",
      },
      {
        heading: "A More Polished Interface",
        description:
          "A round of small styling fixes: import wizards share one " +
          "consistent look, and warning colours match the rest of the app " +
          "everywhere they appear.",
      },
    ],
  },
  {
    version: "0.23.1",
    date: "August 2026",
    features: [
      {
        heading: "Consistent Buttons on Ore, Hydro and Cost Rows",
        description:
          "The edit and delete buttons on ore properties, hydrodynamic " +
          "properties and NPV cost items now use the same drawn icons as the " +
          "rest of the app, rather than symbols borrowed from your operating " +
          "system. Delete turns red when you hover it, so it is harder to hit " +
          "by mistake.",
      },
      {
        heading: "Clear All No Longer Prompts for Nothing",
        description:
          "On the Kinetics page, Clear All Ore Properties stayed clickable " +
          "when the list was already empty and opened a confirmation for no " +
          "reason. It is now greyed out until there is something to clear.",
      },
    ],
  },
  {
    version: "0.23.0",
    date: "August 2026",
    features: [
      {
        heading: "You'll Know When You're Added to a Project",
        description:
          "Being added to a project now reaches you straight away — a note in " +
          "the bell menu and an email, with a link that opens the project. You " +
          "can turn either off under Notifications in Settings.",
      },
      {
        heading: "A Cleaner Interface Throughout",
        description:
          "Every icon in the navigation is now drawn to match the rest of the " +
          "app, instead of borrowed from your operating system — so Orecasting " +
          "looks the same whether you are on Windows, a Mac or a phone. Page " +
          "and panel headings are plainer and a little smaller, which leaves " +
          "more of each screen for your actual work.",
      },
      {
        heading: "Easier Forms and Dialogs",
        description:
          "Boxes you type into now have a visible edge everywhere — on a few " +
          "screens the outline was missing entirely and the field blended into " +
          "the panel behind it. Dialogs keep the keyboard inside them while " +
          "open, so tabbing no longer wanders off behind the dialog, and every " +
          "dialog now has a clear close button.",
      },
      {
        heading: "Kinetics Chart Colours",
        description:
          "New ore types are given colours chosen to stay apart from one " +
          "another — including for colourblind readers — rather than the bright " +
          "rainbow used before. You can still set any colour you like per ore " +
          "type from its Plot Color picker.",
      },
      {
        heading: "A More Polished Interface",
        description:
          "The Water Balance demo on the home page now matches the real " +
          "screen, and the notification bell, the project Edit button and the " +
          "admin menu all pick up the new icon set.",
      },
    ],
    note:
      "The new chart colours apply to ore types you add from now on — anything " +
      "already in a scenario keeps the colour it has, so your saved charts " +
      "look exactly as you left them.",
  },
  {
    version: "0.22.0",
    date: "August 2026",
    features: [
      {
        heading: "A Clearer Water Balance Diagram",
        description:
          "Heaps, ponds and plants now each have their own shape, so you can " +
          "read the circuit at a glance without matching colours to a key. " +
          "Every name and percentage on the diagram is drawn in the app's own " +
          "typeface, and pond names now sit on the pond itself with the " +
          "solution type underneath.",
      },
      {
        heading: "Flows That Follow the Page",
        description:
          "Connections run in straight horizontal and vertical legs with " +
          "rounded corners instead of cutting diagonally across the diagram, " +
          "the way a flowsheet is normally drawn. Percentages sit in a label " +
          "on the line, and a faint grid behind everything makes it easier to " +
          "tell where you are as you zoom and pan.",
      },
      {
        heading: "Colour Saved for What Needs You",
        description:
          "Connections that are working correctly are now drawn in a quiet " +
          "grey. Red means a node's outgoing shares do not add up, and amber " +
          "means a connection only carries flow under a condition — so " +
          "anything coloured on the diagram is something worth a look.",
      },
      {
        heading: "More Room, and Details Where You Select Them",
        description:
          "The toolbar now floats on the diagram instead of stacking above " +
          "it, giving the working area noticeably more height. Selecting a " +
          "node or a connection opens a small panel showing its details with " +
          "Edit and Delete, so buttons no longer appear and disappear from " +
          "the top of the page and shift the diagram under your cursor.",
      },
      {
        heading: "A More Polished Interface",
        description:
          "Fixed a problem where the diagram could stop responding in the " +
          "right places after showing or hiding the project sidebar, and " +
          "tidied the spacing, buttons and warnings across the page.",
      },
    ],
    note:
      "Only how the water balance is drawn has changed — your nodes, " +
      "connections and percentages are exactly as you left them.",
  },
  {
    version: "0.21.0",
    date: "August 2026",
    features: [
      {
        heading: "The Rest of the App on a Phone",
        description:
          "Following the Tasks & Board work in the last release, the same " +
          "treatment now covers My Tasks, the document register, " +
          "maintenance, operational data and the admin pages. Wide tables " +
          "become a card per row on a phone — each value on its own labelled " +
          "line, with the buttons for that row underneath — so nothing sits " +
          "off the side of the screen where it cannot be reached.",
      },
      {
        heading: "Built for Touch",
        description:
          "Buttons, checkboxes and pickers are bigger on touch screens, and " +
          "anything that used to explain itself only when you hovered a " +
          "mouse over it now says so on screen. Tapping a bar on the " +
          "portfolio timeline or a day on a calendar opens its details " +
          "instead of doing nothing.",
      },
      {
        heading: "Calendars That Fit",
        description:
          "The tasks calendar switches to status dots and an under-the-grid " +
          "day sheet on a phone, and the board's calendar view falls back to " +
          "an agenda list on narrow windows, where a seven-column month grid " +
          "left task names unreadable.",
      },
      {
        heading: "Charts and Forms That Behave",
        description:
          "Admin usage charts turn their bars sideways on a phone so the " +
          "company and project names stay readable. Tapping into a search " +
          "box or a form field no longer zooms the page in and leaves it " +
          "there, and long company names no longer stretch a filter off the " +
          "edge of the screen.",
      },
    ],
  },
  {
    version: "0.20.1",
    date: "August 2026",
    features: [
      {
        heading: "Tasks & Board on a Small Screen",
        description:
          "Every view on the Tasks & Board page — list, board, timeline, " +
          "table, calendar, roadmap, workload, reports and dependencies — " +
          "now fits the window instead of running off the side of it. " +
          "Controls wrap onto a second line rather than being pushed out of " +
          "reach, and the wider views scroll inside their own panel so the " +
          "page itself stays put. The Templates page got the same treatment.",
      },
      {
        heading: "One Menu on Mobile",
        description:
          "On a phone or a narrow window, the project navigation now opens " +
          "from the same menu button as the rest of the app. The old pull " +
          "tab on the edge of the screen is gone — it sat where the browser " +
          "listens for a swipe back, so it often could not be tapped at all. " +
          "Admin pages work the same way.",
      },
      {
        heading: "A More Polished Interface",
        description:
          "Date pickers, status and assignee menus, and the task detail " +
          "dialog all behave properly at narrow widths, and the timeline " +
          "keeps more of the schedule on screen. Nothing changed on a " +
          "desktop-sized window.",
      },
    ],
  },
  {
    version: "0.20.0",
    date: "August 2026",
    features: [
      {
        heading: "Closure & Draindown",
        description:
          "A new Analysis & Economics page carries a finished simulation " +
          "past the end of leaching. Start from any saved snapshot, set a " +
          "closure horizon, and model rinsing, draindown, and outflow " +
          "quality with the same physics as the main engine — draw exactly " +
          "where each rinse phase irrigates, optionally keep extracting " +
          "with your kinetics, keep the ponds and plants operating, and " +
          "watch the pad drain in the 3D viewer.",
      },
      {
        heading: "Closure Results in Excel Exports",
        description:
          "Custom Export sheets can now draw from a completed closure run " +
          "— mix closure and simulation sheets in one workbook, chart the " +
          "draindown, and the Overview sheet gains a Closure & Draindown " +
          "summary.",
      },
      {
        heading: "File Exports in the Document Register",
        description:
          "A finished export can be filed straight into the project's " +
          "Document Register — as a new numbered document or the next " +
          "revision of an existing one — and then flows through approvals, " +
          "issue, and transmittals like any other controlled document.",
      },
      {
        heading: "A Live Notification Bell",
        description:
          "The bell now updates the moment something happens — no refresh " +
          "needed — and notifications can be cleared one at a time or all " +
          "at once.",
      },
      {
        heading: "Show Only the Pages You Use",
        description:
          "Project administrators can now hide individual pages, not just " +
          "whole sections — keep NPV & Cashflow while hiding Ensembles, " +
          "for example. Nothing is deleted; re-enabling a page restores " +
          "it with its data intact.",
      },
      {
        heading: "A More Polished Help Center",
        description:
          "A new Closure & Draindown chapter with figures, updated export " +
          "and project chapters, and corrected formatting throughout the " +
          "later chapters.",
      },
    ],
    note:
      "A closure analysis is a full cloud computation over its horizon — " +
      "comparable in cost to a simulation run of the same length.",
  },
  {
    version: "0.19.0",
    date: "August 2026",
    features: [
      {
        heading: "NPV & Cashflow",
        description:
          "A new Analysis & Economics section values your simulations. " +
          "Build an open-ended cost model for a scenario — mining, " +
          "reagents, royalties, one-time capital, anything — and every " +
          "completed run is turned into a yearly cashflow with NPV, IRR, " +
          "and payback, using your metal prices.",
      },
      {
        heading: "Kinetics Fitting",
        description:
          "Paste lab column-test data and fit it to the platform's kinetic " +
          "models directly — single tests or whole multi-size programs, " +
          "including an overall equation blended from your crush size " +
          "distribution. Apply a fit to an ore property with one click.",
      },
      {
        heading: "Sensitivity Ensembles",
        description:
          "Ask what-if questions without touching your scenario: define " +
          "named variants that scale recovery, leach rate, hydraulic " +
          "properties, metal prices, precipitation, evaporation, or " +
          "irrigation rate, run them side by side, and compare any results " +
          "column across all variants on one chart. The number of " +
          "simulation runs is always shown before you start.",
      },
      {
        heading: "Reconciliation",
        description:
          "Close the loop between forecast and reality. Map your " +
          "Operational Data columns to simulation results once, and the " +
          "new Reconciliation page lines the model up against site actuals " +
          "day by day — with cumulative variance, bias, and error " +
          "statistics per quantity.",
      },
      {
        heading: "A Reorganised Sidebar",
        description:
          "Engineering is now Heap Leach, site-wide inputs live in their " +
          "own Site & Water section (Climate, Water Balance), and the new " +
          "analysis pages sit under Analysis & Economics. Everything is " +
          "where it was — just easier to find — and the Help Center has " +
          "full chapters on every new page.",
      },
    ],
    note:
      "Ensemble runs start one simulation per variant, so a 5-variant " +
      "ensemble uses 5 runs' worth of compute — the page shows the count " +
      "before you press Run.",
  },
  {
    version: "0.18.1",
    date: "August 2026",
    features: [
      {
        heading: "Sidebars Tailored to Each Project",
        description:
          "Projects now show only the sections they actually use. An " +
          "administrator can switch off whole areas of the sidebar — such " +
          "as Compliance or Operation — for a project, and they disappear " +
          "for everyone working in it. The Overview is always available, " +
          "and nothing is deleted: switching a section back on restores it " +
          "exactly as it was.",
      },
    ],
    note:
      "If a section you use has disappeared from a project's sidebar, it " +
      "has been turned off by an administrator — get in touch with them " +
      "if you still need it.",
  },
  {
    version: "0.18.0",
    date: "August 2026",
    features: [
      {
        heading: "Notifications, Your Way",
        description:
          "Notification settings are no longer simple on/off switches. Every " +
          "kind of task notification — assignments, @mentions, comments, " +
          "status changes, completions, due-date reminders, overdue alerts, " +
          "and the weekly digest — now has its own delivery picker: in-app " +
          "bell, email, or both. Deselect everything to mute just that kind. " +
          "SMS is on the way.",
      },
      {
        heading: "Different Projects, Different Rules",
        description:
          "A project selector at the top of the notification settings lets " +
          "any project carry its own choices — for example, email alerts " +
          "from your busiest project while everything else stays in-app. " +
          "Customized rows are tagged so you can see at a glance what " +
          "differs, and one click puts a row or a whole project back on " +
          "your defaults.",
      },
      {
        heading: "A More Polished Board",
        description:
          "The assignee, status, and @mention pickers on the task board now " +
          "close when you click elsewhere or press Escape.",
      },
    ],
  },
  {
    version: "0.17.0",
    date: "August 2026",
    features: [
      {
        heading: "Six New Ways to See the Board",
        description:
          "A new More menu on the task board adds a sortable Table view, a " +
          "monthly Calendar, a milestone Roadmap for client meetings, a " +
          "Workload grid showing each person's hours week by week, a " +
          "Reports dashboard, and a Dependencies chain view. The My Tasks " +
          "page also gains a Portfolio timeline of every project you're on.",
      },
      {
        heading: "Plan vs. Reality",
        description:
          "Set a schedule baseline and the timeline shows how far each " +
          "task has drifted from the agreed plan, with a slippage report " +
          "for the ones running late. Log actual hours on any task and " +
          "compare them against the estimate as the work happens.",
      },
      {
        heading: "Reminders That Find You",
        description:
          "The bell now warns you the day before a task is due and again " +
          "if it goes overdue. New notification settings let you turn " +
          "assignment emails or the weekly digest off, or mute individual " +
          "projects entirely.",
      },
      {
        heading: "Smoother Teamwork",
        description:
          "Type @ in a comment and a member picker appears as you type. " +
          "Bulk edits apply in one step, and moving a task that others " +
          "depend on offers to shift the whole downstream chain with it. " +
          "Every task now keeps a history of who changed what, and boards " +
          "export to a spreadsheet with one click.",
      },
      {
        heading: "More Connected Tasks",
        description:
          "Tasks can now link to scenarios and simulation runs alongside " +
          "documents and external pages, and your saved board templates " +
          "have their own management page for previewing, renaming, and " +
          "tidying up.",
      },
    ],
  },
  {
    version: "0.16.0",
    date: "August 2026",
    features: [
      {
        heading: "A Collaborative Task Board",
        description:
          "Tasks can now be assigned to several people at once, and every " +
          "task has a discussion thread with @mentions. A notification " +
          "bell in the header keeps you on top of assignments, status " +
          "changes, comments, and mentions — with an email when you're " +
          "assigned and a weekly summary of your open tasks. You can also " +
          "watch any task to follow it without being assigned.",
      },
      {
        heading: "Task Details Editor",
        description:
          "Click any task (or Kanban card) to open a full editor for its " +
          "description, schedule, assignees, and everything else — task " +
          "rows stay clean and are no longer edited by accident.",
      },
      {
        heading: "Documents and Links on Tasks",
        description:
          "Attach documents from the project's Document Register or " +
          "external links (like SharePoint) to any task. A linked " +
          "document can even drive the task for you — submitting it for " +
          "review or issuing it moves the task along automatically.",
      },
      {
        heading: "Board Templates and Effort Estimates",
        description:
          "Save a whole task board as a reusable template — private or " +
          "shared with your company — and start new projects from it in " +
          "one click. Tasks now take an estimated effort in hours, and " +
          "the new team strip shows each member's open tasks and total " +
          "estimated hours at a glance.",
      },
      {
        heading: "Task Visibility and Bulk Editing",
        description:
          "Control who sees each task — the whole project, only your " +
          "company, or just the assignees. A new Select mode updates " +
          "many tasks at once: set status, add an assignee, or shift " +
          "schedules by days. You'll find the board under Management → " +
          "Tasks & Board, and the Help Center covers everything new.",
      },
    ],
  },
  {
    version: "0.15.3",
    date: "August 2026",
    features: [
      {
        heading: "Plant Recovery Fix for Mixed-Case Metal Symbols",
        description:
          "Process plants could recover zero metal when the metal symbol " +
          "on the plant's efficiency settings was capitalized differently " +
          "than the metal itself (\"AU\" vs \"Au\") — solution passed " +
          "through the plant without recovery. Plant recovery now matches " +
          "metals regardless of capitalization, completing the fixes from " +
          "the last two releases.",
      },
    ],
    note:
      "If a simulation showed dissolved metal but zero plant recovery, " +
      "re-run it on this version.",
  },
  {
    version: "0.15.2",
    date: "August 2026",
    features: [
      {
        heading: "Complete Fix for Zero Extraction Results",
        description:
          "The previous release's fix for metal symbols capitalized " +
          "differently in different places (\"Au\" vs \"AU\") resolved the " +
          "recoverable metal calculation but could still leave daily " +
          "extraction at zero. Extraction now works correctly for those " +
          "scenarios too.",
      },
    ],
    note:
      "If a simulation on 0.15.1 showed recoverable metal but zero " +
      "extracted ounces, re-run it on this version.",
  },
  {
    version: "0.15.1",
    date: "August 2026",
    features: [
      {
        heading: "Metal Recovery Fix for Mixed-Case Metal Symbols",
        description:
          "Simulations could report zero recoverable metal when a metal's " +
          "symbol was capitalized differently in different places (for " +
          "example \"Au\" vs \"AU\"). Extraction curves now match your " +
          "metals regardless of capitalization.",
      },
      {
        heading: "Clearer Errors for Unused Extraction Curves",
        description:
          "If an ore property carries an extraction curve for a metal " +
          "that isn't part of the scenario, the simulation now stops with " +
          "a clear message naming the curve, instead of quietly ignoring " +
          "it.",
      },
    ],
    note:
      "If a past simulation unexpectedly reported zero extraction for a " +
      "metal, re-run it on this version — it will now recover metal " +
      "correctly.",
  },
  {
    version: "0.15.0",
    date: "August 2026",
    features: [
      {
        heading: "My Tasks — All Your Work in One Place",
        description:
          "A new My Tasks page in the top navigation shows every task " +
          "assigned to you across all of your projects, grouped by project " +
          "with quick filters for project, status, and a search box. " +
          "Overdue work is flagged so nothing slips by.",
      },
      {
        heading: "Personal To-Dos",
        description:
          "Keep your own to-do list right alongside your project work. " +
          "Personal tasks aren't tied to any project — add, edit, and " +
          "complete them from the My Tasks page, with optional start and " +
          "due dates.",
      },
      {
        heading: "Calendar View",
        description:
          "Switch My Tasks to a monthly calendar to see everything laid " +
          "out by due date — today is highlighted, overdue items stand " +
          "out, and you can step month by month.",
      },
      {
        heading: "Jump Straight to the Right Project",
        description:
          "From My Tasks, click a project's name to open its overview, or " +
          "click any task to land on that project's Management board with " +
          "the project already selected.",
      },
    ],
  },
  {
    version: "0.14.3",
    date: "August 2026",
    features: [
      {
        heading: "Refreshed Sign-In Window",
        description:
          "The secure sign-in window now carries the Orecasting brand — " +
          "including the Powered by SLR mark — so it matches the rest of " +
          "the app. Nothing changes about how you sign in.",
      },
    ],
  },
  {
    version: "0.14.2",
    date: "August 2026",
    features: [
      {
        heading: "Plant Flow Rate Fixed in Custom Exports",
        description:
          "The Plant Solution Flow Rate column on the Water Balance sheet " +
          "of custom Excel exports now reports the solution flowing through " +
          "each plant. Previously it could read zero for every day even " +
          "when the plant was processing solution.",
      },
    ],
    note:
      "Saved export templates that include this column keep the old " +
      "behaviour — re-add the Plant Solution Flow Rate column (or rebuild " +
      "the template from a built-in one) to pick up the fix.",
  },
  {
    version: "0.14.1",
    date: "August 2026",
    features: [
      {
        heading: "Heap Absorption Tracking",
        description:
          "Results and custom exports now include an Absorption Demand " +
          "column for each heap: the total solution the ore placed to date " +
          "will soak up as it wets from its as-placed moisture to residual. " +
          "Together with the existing Absorption Deficit column it shows " +
          "how much has already been absorbed and how much is still to come.",
      },
      {
        heading: "Software Version on Every Run",
        description:
          "Simulations and exports now record the software version that " +
          "produced them. You'll find it in the Run Info panel and in the " +
          "Run Configuration section of exported workbooks — useful when " +
          "comparing runs made before and after an update.",
      },
    ],
    note:
      "The new Absorption Demand column appears on simulations run from " +
      "this version onward — re-run a scenario to see it on existing work.",
  },
  {
    version: "0.14.0",
    date: "August 2026",
    features: [
      {
        heading: "Snapshot Image Export",
        description:
          "The 3D snapshot viewer can now export presentation-ready images " +
          "of the heap. Open the new Export Image panel to pick a camera " +
          "angle (or frame your own), choose what the colors represent, and " +
          "download the view with its legend and date included.",
      },
      {
        heading: "Backgrounds and Custom Colors",
        description:
          "Exported images can use any background color — or none at all, " +
          "for a transparent image that drops straight into slides and " +
          "reports. You can also set the color scale's range and pick your " +
          "own low and high colors for the gradient, such as silver to gold.",
      },
      {
        heading: "Flexible Sizes and Formats",
        description:
          "Export at standard resolutions up to 4K or enter exact pixel " +
          "dimensions, in PNG, JPEG, or WebP format.",
      },
      {
        heading: "Metal Grade Legend",
        description:
          "The snapshot viewer now shows a color legend when viewing the " +
          "heap by metal grade.",
      },
    ],
  },
  {
    version: "0.13.2",
    date: "August 2026",
    features: [
      {
        heading: "Project Team on Custom Exports",
        description:
          "Custom Excel exports can now include a Project Team section on " +
          "the overview page: who generated the export, plus the project's " +
          "members with their job titles and companies. Turn it on from the " +
          "Overview step of the export wizard — it is off by default and can " +
          "be saved into your export templates.",
      },
      {
        heading: "Project Name on Export Workbooks",
        description:
          "Exported workbooks now show which project they came from: the " +
          "project name appears in the overview page header alongside the " +
          "scenario, and in the Run Configuration section.",
      },
    ],
    note:
      "Job titles come from each member's user profile — if the Project " +
      "Team section shows blanks, ask your team to fill in the Title field " +
      "in their profile settings.",
  },
  {
    version: "0.13.1",
    date: "August 2026",
    features: [
      {
        heading: "Accurate Simulation Run Durations",
        description:
          "Simulation history and usage pages now report how long a run " +
          "actually took to compute. Previously the duration could also " +
          "count time spent waiting in the queue and the time until the " +
          "results were next opened, so a short run could show as many " +
          "hours.",
      },
      {
        heading: "Correct Company Names on Project Teams",
        description:
          "The project Members page now shows each member's own company. " +
          "On projects shared between companies, partner members were " +
          "sometimes labeled with the project's main company instead of " +
          "their own.",
      },
    ],
  },
  {
    version: "0.13.0",
    date: "August 2026",
    features: [
      {
        heading: "User Badges",
        description:
          "Recognize your teammates: administrators can now create custom " +
          "badges — a name, an icon, a color, and a short description — and " +
          "award them to users. Badges appear next to people's names " +
          "across the app, including project teams and company member " +
          "lists. Hover a badge to see what it was awarded for.",
      },
      {
        heading: "An Icon for Every Discipline",
        description:
          "The badge designer includes nearly a hundred icons organized by " +
          "discipline — development, project management, engineering and " +
          "science, operations and field work, and more — plus achievement " +
          "medals and trophies. You can also type in any emoji of your own.",
      },
      {
        heading: "Board View for Project Tasks",
        description:
          "The Management page now has a Board view alongside List and " +
          "Timeline: one column per status, with drag-and-drop to move " +
          "tasks between them. Cards show assignees, due dates (overdue " +
          "ones highlighted), subtask progress, and milestones.",
      },
      {
        heading: "Kinetic Tables in Custom Exports",
        description:
          "Custom Excel exports can now include a Kinetic Tables sheet " +
          "with the extraction tables behind your scenario's ore " +
          "properties, day by day — as daily or cumulative values. Enable " +
          "it from the Overview step of the export wizard.",
      },
    ],
  },
  {
    version: "0.12.2",
    date: "August 2026",
    features: [
      {
        heading: "Heap Import Errors Show Their Icon Again",
        description:
          "When a heap import fails, or finishes with warnings, the status " +
          "icon on the final step now displays properly instead of a " +
          "garbled character. The messages themselves are unchanged — only " +
          "the icon beside them was affected.",
      },
      {
        heading: "Behind the Scenes",
        description:
          "Fixed an intermittent failure in our internal simulation test " +
          "suite, where a check could run before the simulation it was " +
          "inspecting had settled. This affects our automated testing only " +
          "and does not change how simulations run for you.",
      },
    ],
  },
  {
    version: "0.12.1",
    date: "August 2026",
    features: [
      {
        heading: "See Who Locked a Scenario at a Glance",
        description:
          "Locked scenario cards now show the name of the person who " +
          "locked the scenario and the date it was locked, right on the " +
          "card — no need to hover for the tooltip. The name shows " +
          "correctly even when the locker works for a different company " +
          "on the project.",
      },
      {
        heading: "A Safeguard When Unlocking Someone Else's Lock",
        description:
          "Unlocking a scenario that another user locked now asks for " +
          "confirmation first, naming who locked it and when. This makes " +
          "it clear you are breaking the chain of custody of their lock — " +
          "and that you will be recorded as the person who unlocked it. " +
          "Unlocking your own lock works instantly, as before.",
      },
    ],
  },
  {
    version: "0.12.0",
    date: "August 2026",
    features: [
      {
        heading: "Lock Verified Scenarios",
        description:
          "Once a scenario's results have been checked and signed off, a " +
          "user with the new lock permission can lock it from the project " +
          "overview. A locked scenario and everything in it — heaps, " +
          "properties, water balance, climate and results — becomes " +
          "read-only for everyone, including administrators, until it is " +
          "unlocked again.",
      },
      {
        heading: "See Who Locked It, and When",
        description:
          "Locked scenarios carry a clear Locked badge on the project " +
          "overview and a lock marker next to the scenario name on every " +
          "page. Hovering the badge shows who locked the scenario and on " +
          "what date, and the same is recorded when it is unlocked.",
      },
      {
        heading: "Duplicate to Keep Working",
        description:
          "Anyone can still duplicate a locked scenario and continue " +
          "experimenting in the copy — the original stays frozen exactly " +
          "as verified, and the copy starts unlocked.",
      },
      {
        heading: "A Tidier Scenario Picker",
        description:
          "Archived scenarios no longer clutter the sidebar's scenario " +
          "dropdown — they remain available from the project overview.",
      },
    ],
    note:
      "Locking also pauses new simulation runs on that scenario, so the " +
      "verified results always stay the latest ones. Unlock the scenario " +
      "or run in a duplicate if you need fresh runs.",
  },
  {
    version: "0.11.1",
    date: "August 2026",
    features: [
      {
        heading: "Heap Import Handles Imperfect Spreadsheets",
        description:
          "Importing a heap from Excel no longer breaks when the file " +
          "contains stray rows without ore data — a common side effect of " +
          "formula columns extending past the last real row. Such rows " +
          "are now skipped automatically and the import summary tells you " +
          "how many were left out, so Total Ore Mass and metal placed " +
          "always show real numbers.",
      },
    ],
    note:
      "If a recent heap import showed NaN for Total Ore Mass or metal " +
      "placed, delete that heap and re-import the same file — the new " +
      "import will produce correct totals.",
  },
  {
    version: "0.11.0",
    date: "August 2026",
    features: [
      {
        heading: "Export Only the Dates You Need",
        description:
          "Sheets and charts in the Custom Export can now be limited to a " +
          "date range — build a sheet of just 2030's flows, or plot " +
          "cumulative recovery for a chosen window. Cumulative columns " +
          "still count from the start of the run, so recovery-to-date " +
          "stays truthful.",
      },
      {
        heading: "Charts Worth Presenting",
        description:
          "Exported Excel charts have been redesigned: larger, with " +
          "readable axis numbers like 4.5M instead of walls of digits, " +
          "the legend beneath the chart instead of over your data, " +
          "clearly distinct series colors, and angled date labels that " +
          "never run together.",
      },
      {
        heading: "A Smoother Export Wizard",
        description:
          "Pick attributes on the left and watch your sheet build on the " +
          "right, with friendly attribute names and proper unit symbols. " +
          "The Generate button is always in view, warnings only appear " +
          "once they're relevant, and every chart previews with the same " +
          "colors Excel will use.",
      },
      {
        heading: "A More Polished Interface",
        description:
          "Dozens of small refinements across the export wizard: tidier " +
          "cards and spacing, quieter delete buttons, clearer axis " +
          "selection on charts, and better keyboard and screen-reader " +
          "support.",
      },
    ],
    note:
      "If a previous export's chart showed a legend full of dates instead " +
      "of the series name, that is fixed — re-running the export will " +
      "produce a corrected workbook.",
  },
  {
    version: "0.10.0",
    date: "August 2026",
    features: [
      {
        heading: "Export Templates for Your Whole Company",
        description:
          "Excel export templates can now be shared at the company level, " +
          "so every project team works from the same report formats. " +
          "Company templates sit alongside your personal and project " +
          "templates in the export wizard.",
      },
      {
        heading: "Templates That Travel Between Simulations",
        description:
          "Template columns now carry over between simulations and " +
          "scenarios. If a template references something a simulation " +
          "doesn't have, the export flags the gap clearly and completes " +
          "anyway instead of failing — and you can re-run a finished " +
          "export with one click.",
      },
      {
        heading: "More Expressive Export Charts",
        description:
          "Stacked bar charts, a per-chart time period (daily, weekly, " +
          "monthly, or yearly on each chart), custom series colors and " +
          "dashed line styles, pond maximum-volume reference lines, and " +
          "new daily built-in charts including Pond Volumes.",
      },
      {
        heading: "More Polished Workbooks",
        description:
          "Exported workbooks now open with a branded title block, grouped " +
          "section bands across related columns, header accent colors, and " +
          "in-cell data bars that make big numbers stand out at a glance.",
      },
      {
        heading: "A Safety Check for Chained Plants",
        description:
          "The water balance editor now warns if one process plant's " +
          "outflow can reach another plant. Plants chained in series are " +
          "not supported — solution quietly collects in the second plant " +
          "and leaves the circuit. The same warning appears when saving " +
          "and when a simulation starts.",
      },
    ],
    note:
      "If your water balance shows the new chained-plant warning, existing " +
      "results for that scenario have been losing circulating solution " +
      "silently — please reroute each plant to a pond or heap and re-run.",
  },
  {
    version: "0.9.0",
    date: "August 2026",
    features: [
      {
        heading: "Key Operational Numbers on the Export Overview",
        description:
          "Excel exports can now open with the numbers engineers usually dig " +
          "out of the daily sheets by hand: per-heap peaks with the date they " +
          "happened (highest injection, outflow, and makeup-water days, plus " +
          "peak pore water and saturated cells), pond and plant peaks " +
          "(highest pregnant flow, peak pond volume and days above capacity, " +
          "lowest pond level, richest and average solution grades), and how " +
          "long each heap actually spent leaching.",
      },
      {
        heading: "Recovery Milestones at a Glance",
        description:
          "The Overview now reports when each metal reached 50%, 75%, and " +
          "90% of its recoverable total, and where recovery ended up — no " +
          "more reading it off the curve by eye.",
      },
      {
        heading: "A Water Balance Summary",
        description:
          "One table totals the run's water story: irrigation applied, " +
          "evaporation (and what share of applied water it took), " +
          "precipitation with the biggest storm day, makeup water used and " +
          "how many days needed it, plus how much water each heap absorbed " +
          "and still holds.",
      },
      {
        heading: "These Work on Your Existing Results",
        description:
          "The new Overview sections are computed from the stored daily " +
          "results when the export runs, so they appear for simulations you " +
          "have already completed — no re-run needed. They're on by default " +
          "for new exports and in the built-in templates; each section can " +
          "be toggled in the export wizard.",
      },
      {
        heading: "3D Heap Models by Default",
        description:
          "The built-in export templates now include a rotatable 3D model " +
          "of each heap, colored by elevation, on the Overview sheet " +
          "(rendered in Excel desktop). Remove it or switch the coloring in " +
          "the export wizard if you prefer.",
      },
    ],
    note:
      "Peak metrics respect the report date range you set in the wizard. " +
      "Sections skip any metric your simulation's results don't carry, so " +
      "older runs may show fewer rows.",
  },
  {
    version: "0.8.6",
    date: "August 2026",
    features: [
      {
        heading: "Irrigation Evaporation Displays Correctly",
        description:
          "The irrigation evaporation value shown in results charts and " +
          "included in Excel exports was displayed 100 times larger than " +
          "the true setting (for example, 7% could appear as 700%). It now " +
          "shows the correct percentage. Simulation results themselves were " +
          "never affected — only how this one value was displayed.",
      },
      {
        heading: "Help Center in the User Menu",
        description:
          "The Help Center is now one click away: open the menu under your " +
          "name in the top-right corner and choose Help Center. It's in " +
          "the mobile menu too.",
      },
      {
        heading: "Clearer Water Balance Warnings",
        description:
          "The water balance panel now says \"water may be lost\" only for " +
          "issues that can actually lose water. Other findings — like a " +
          "pond that may exceed its capacity — are flagged as \"review " +
          "recommended\" instead.",
      },
    ],
    note:
      "The corrected evaporation display applies to newly run simulations. " +
      "Re-run an older simulation to see the corrected value in its charts " +
      "and exports.",
  },
  {
    version: "0.8.5",
    date: "August 2026",
    features: [
      {
        heading: "Water and Metal Are Fully Accounted For",
        description:
          "Two accounting gaps in the simulation engine have been closed. " +
          "Scenarios using irrigation evaporation no longer over-count the " +
          "metal riding on evaporated water, and ponds that reach their " +
          "maximum volume now hold the extra solution instead of quietly " +
          "turning it away. Expect slightly different — more accurate — " +
          "numbers in affected scenarios.",
      },
      {
        heading: "Ponds Can Exceed Their Maximum Volume",
        description:
          "A pond's maximum volume now works as a capacity flag rather than " +
          "a hard wall: inflow beyond capacity is kept, and the run log " +
          "tells you the pond overfilled and by how much — your cue to " +
          "re-scope the pond or add an overflow route.",
      },
      {
        heading: "The Run Log Tells You When Water Goes Missing",
        description:
          "If a heap's outflow routing covers less than 100% — so part of " +
          "its drainage has nowhere to go — the simulation now says so in " +
          "the run log, with the day and the amount, instead of dropping it " +
          "silently.",
      },
      {
        heading: "Bad Input Data Is Caught Before the Run",
        description:
          "Simulations now check extraction curves and metal definitions up " +
          "front: missing or unreadable kinetics files, negative or " +
          "over-100% extraction values, and duplicate metal symbols stop " +
          "the run with a clear message instead of finishing with wrong " +
          "numbers. Plant recovery assumptions are also flagged in the run " +
          "log when a time-series is missing an efficiency column.",
      },
    ],
    note:
      "If a previously working scenario now fails validation, its kinetics " +
      "data has a genuine problem that used to be silently ignored — the " +
      "error message says exactly what to fix. Results for scenarios with " +
      "irrigation evaporation or capped ponds will differ slightly from " +
      "earlier releases; the new numbers are the correct ones.",
  },
  {
    version: "0.8.4",
    date: "August 2026",
    features: [
      {
        heading: "Result Names Now Say What They Measure",
        description:
          "Several results columns have been renamed so the name matches " +
          "the number. \"Barren Solution Used\" is now \"Barren Solution " +
          "Applied\" (what actually reached the pad), pond \"Tenor\" is now " +
          "\"Inflow Tenor\" (the grade of what flowed in that day), pond " +
          "\"Volume\" is now \"Volume (Peak)\" (the most the pond held " +
          "before its daily release, alongside the end-of-day volume), and " +
          "the precipitation columns now share one prefix so the full set " +
          "is easy to spot.",
      },
      {
        heading: "See Barren Water Leave the Pond, Not Just Arrive at the Pad",
        description:
          "A new \"Barren Solution Drawn\" result reports how much solution " +
          "was pulled from your pond(s) toward each heap every day. Together " +
          "with \"Barren Solution Applied\" and the in-transit column, the " +
          "chain from pond withdrawal to pad irrigation is fully auditable.",
      },
      {
        heading: "True Pond Grade",
        description:
          "Ponds now report a \"Stored Tenor\" — the concentration of what " +
          "the pond actually holds at the end of each day. Use it to see the " +
          "grade feeding your plant; the previous tenor column only showed " +
          "the concentration of that day's inflow.",
      },
    ],
    note:
      "Results produced before this update keep their old column names and " +
      "display normally. If a custom export template or saved chart " +
      "references an old name (for example \"barren_solution_used\"), " +
      "re-select the renamed column once and it will pick up new runs.",
  },
  {
    version: "0.8.3",
    date: "August 2026",
    features: [
      {
        heading: "Barren Ponds Hold Their Water Until Leaching Needs It",
        description:
          "Flow from a pond to a heap is now limited by the leach cycles' " +
          "actual demand. Previously a pond connected to a heap sent its " +
          "entire stored volume toward the heap every day — draining ponds " +
          "to zero before leaching even started. Ponds now keep their " +
          "solution and release exactly what the day's irrigation will use.",
      },
      {
        heading: "New Heap Water Inventory Results",
        description:
          "Each heap now reports a daily breakdown of the water it holds: " +
          "locked-in moisture, drainable solution (with its fast and slow " +
          "flow-path split), remaining absorption before new ore starts " +
          "releasing water, total drainable capacity, headroom before " +
          "saturation, and solution en route to the next day's irrigation. " +
          "Heap water figures count only ore that has actually been stacked.",
      },
    ],
    note:
      "Re-running scenarios with a pond feeding a heap will show pond " +
      "volumes staying realistically full until leaching draws on them — " +
      "previously that water sat in an internal buffer that results did " +
      "not show. Recovery and metal numbers are unaffected.",
  },
  {
    version: "0.8.2",
    date: "August 2026",
    features: [
      {
        heading: "Pond Outflows Now Report What Actually Moved",
        description:
          "Pond and plant outflow values in results and exports now show the " +
          "water that genuinely moved downstream each day. Previously a " +
          "pond's reported outflow mirrored its inflow even when a plant's " +
          "flow limit held water back, which made pond water balances appear " +
          "to double-count.",
      },
      {
        heading: "End-of-Day Pond Volume Column",
        description:
          "Results now include an end-of-day stored volume for each pond " +
          "alongside the existing daily peak, so inflow minus outflow always " +
          "matches the change in stored volume exactly.",
      },
      {
        heading: "Heap Water Reflects What's In the Cells",
        description:
          "A heap's reported water volume is now the solution actually held " +
          "in its cells, rather than a running total of everything ever " +
          "delivered to it.",
      },
      {
        heading: "Water Balance Setups Are Checked for Leaks",
        description:
          "The water balance editor now flags network setups that can lose " +
          "water while you edit — splits that don't cover 100% of a heap or " +
          "plant's outflow on every day and under every condition, and " +
          "capacity-limited ponds without an overflow route. The same checks " +
          "run when you save and when a simulation starts.",
      },
    ],
    note:
      "If your water balance uses partial splits, plant flow-rate limits, or " +
      "conditions that watch a node's outflow or a heap's volume, re-running " +
      "a simulation may show different pond outflow and heap volume numbers — " +
      "the previous values over-reported outflow. Metal recovery is " +
      "unaffected.",
  },
  {
    version: "0.8.1",
    date: "August 2026",
    features: [
      {
        heading: "More Accurate Process Plant Water Use",
        description:
          "Fluid consumed by a process plant (reagent addition, tailings " +
          "moisture, plant losses) now genuinely leaves the water balance. " +
          "Previously the configured consumption had no effect and the full " +
          "volume was returned to the circuit.",
      },
      {
        heading: "Dissolved Metal Is Never Lost With Plant Fluid",
        description:
          "Metal still in solution after plant recovery now stays in the " +
          "circuit when the plant consumes fluid, instead of disappearing " +
          "with it. New checks verify that every gram of metal is accounted " +
          "for — in the heap, in solution, or recovered — on every day of a " +
          "simulation.",
      },
    ],
    note:
      "If your scenarios use process plant fluid removal, re-running them " +
      "will show different pond volumes and slightly different recoveries — " +
      "the new numbers are the correct ones.",
  },
  {
    version: "0.8.0",
    date: "August 2026",
    features: [
      {
        heading: "Contact Support from the Help Center",
        description:
          "Need help, found a bug, or have a feature idea? The Help Center now " +
          "has a contact form that sends your request straight to our team, and " +
          "you can review the status of everything you've submitted.",
      },
      {
        heading: "AI Assistant Manages the Task Board",
        description:
          "The AI assistant can now work with your project task board — answer " +
          "questions like \"what's overdue?\", add tasks and milestones you ask " +
          "for, mark work done, reschedule, and reassign. Tasks the assistant " +
          "creates are labeled so you always know where they came from, and it " +
          "cannot delete tasks.",
      },
      {
        heading: "Ask About Your Block Model",
        description:
          "Through the assistant you can now ask planning questions before any " +
          "simulation runs — how much ore of each type sits in each lift, what " +
          "tonnage stacks in a given quarter, and what metal is contained where.",
      },
      {
        heading: "Clearer Plant Setup Problems",
        description:
          "If a process plant has no recovery efficiency configured, simulations " +
          "now warn you up front and honestly report 0% efficiency instead of " +
          "silently showing 100% while recovering nothing.",
      },
      {
        heading: "A More Polished Experience",
        description:
          "Fixed a loading spinner that never went away when switching to a " +
          "scenario without heaps, and the heap layout view now reports cell " +
          "dimensions correctly.",
      },
    ],
    note:
      "If a simulation previously showed 100% plant efficiency while recovering " +
      "no metal, re-running it will now report the true 0% — the recovery " +
      "numbers themselves were always correct.",
  },
  {
    version: "0.7.0",
    date: "August 2026",
    features: [
      {
        heading: "AI Assistant Can Build Water Balances",
        description:
          "When connected through the AI assistant integration, you can now describe your " +
          "water routing in plain language — flow splits, storm-pond overflows, and " +
          "conditional rules like \"divert to the plant only while the pond is low\" — and " +
          "the assistant will build or adjust the water balance graph for you on its own " +
          "working copy of your scenario.",
      },
      {
        heading: "AI Scenario Setup End to End",
        description:
          "The assistant can now prepare a complete scenario variation: duplicate your " +
          "scenario, adjust ore extraction curves, generate site climate data from " +
          "historical weather for your coordinates, and name the result after what it " +
          "changed.",
      },
      {
        heading: "AI-Run Simulations, Within Limits",
        description:
          "The assistant can run simulations on the scenario copies it creates and compare " +
          "the results against your baseline — subject to strict per-user daily and " +
          "concurrent run limits, so an assistant can never queue up runaway compute.",
      },
    ],
    note:
      "AI assistants only ever modify their own scenario copies, which are badged ✦ AI " +
      "in the project overview — your scenarios are never edited directly. Review any " +
      "AI-built configuration in the app before relying on its results.",
  },
  {
    version: "0.6.1",
    date: "August 2026",
    features: [
      {
        heading: "Scenario Switching Fully Settles the Leaching View",
        description:
          "After switching scenarios on the Leaching page, the 3D view no longer keeps the " +
          "previous scenario's ground grid behind the new heap, and the playback timeline now " +
          "starts from the new heap's first stacking day instead of staying on the date you had " +
          "been viewing.",
      },
      {
        heading: "Help Center Back Button",
        description:
          "The Back button in the Help Center now returns you to the page you came from, rather " +
          "than stepping back through the help chapters you had visited.",
      },
    ],
  },
  {
    version: "0.6.0",
    date: "August 2026",
    features: [
      {
        heading: "In-App Help Center",
        description:
          "The full Orecasting user manual is now built into the app. Open Help & Documentation " +
          "from the bottom of the project sidebar to browse every chapter — from importing your " +
          "first heap to reading simulation results — with screenshots, step-by-step walkthroughs, " +
          "and a search that takes you straight to the right section.",
      },
      {
        heading: "Smoother Scenario Switching",
        description:
          "Switching scenarios while viewing the Leaching page now loads the new scenario's heap " +
          "immediately. Previously the page could report that no heap data existed until it was " +
          "reloaded.",
      },
      {
        heading: "More Precise Document Control Access",
        description:
          "Issuing controlled documents and changing document register settings are now governed " +
          "solely by their own dedicated permissions, rather than being implied by general project " +
          "editing access. Project owners have been granted these permissions automatically, so no " +
          "action is needed — but if someone on your team unexpectedly loses a document control " +
          "action, a project owner can grant the specific permission from the team page.",
      },
      {
        heading: "Faster, More Reliable Service",
        description:
          "Behind-the-scenes improvements reduce repeated security round-trips in the browser and " +
          "give our team better visibility into service health, helping us find and fix issues " +
          "sooner.",
      },
    ],
    note:
      "Simulations run on or after August 5 include a correction to how evaporation interacts " +
      "with dissolved metal during irrigation. Comparing against results generated before that " +
      "date may show small differences in recovered metal — re-running the simulation brings it " +
      "onto the corrected physics.",
  },
  {
    version: "0.5.5",
    date: "August 2026",
    features: [
      {
        heading: "Every Team Permission Can Now Be Granted",
        description:
          "Document control, project banner and simulation renaming permissions appeared in the " +
          "member permissions list but could not actually be saved — the change was rejected. All " +
          "of them now save correctly. If a team member already held one of these, their " +
          "permissions were impossible to edit at all; those members can be edited again.",
      },
      {
        heading: "Stronger Separation Between Projects",
        description:
          "Several checks that decide which project a request is allowed to touch have been " +
          "tightened, so project data stays firmly within the project and company it belongs to. " +
          "Audit records for document control settings and bulk imports are now visible only to " +
          "the project's own company rather than to every partner on a joint venture.",
      },
      {
        heading: "Safer Document Uploads",
        description:
          "Document revision uploads are now validated more strictly, and importing a document " +
          "register no longer quietly replaces your project's status and category lists — imported " +
          "values are added to what you already have, and changing those lists still requires " +
          "document control settings access.",
      },
      {
        heading: "Clearer Admin Role Controls",
        description:
          "Administrator access is now driven entirely by the Role setting, so the role and the " +
          "permission list can no longer disagree. Changing someone's role updates their access " +
          "immediately, and the admin screen explains which settings apply to which role.",
      },
      {
        heading: "A More Polished Interface",
        description:
          "When a permission change cannot be saved, the message now says exactly which permission " +
          "was rejected instead of a generic error.",
      },
    ],
    note:
      "If you connect to Orecasting through the API or an AI assistant integration rather than " +
      "the web app, requests that name one project in the address and a different one in the " +
      "request headers are now refused instead of being resolved silently. Ordinary use of the " +
      "web app is unaffected.",
  },
  {
    version: "0.5.4",
    date: "July 2026",
    features: [
      {
        heading: "Results Page Opens Reliably",
        description:
          "The Results page and comparison view no longer show a permissions error in the " +
          "remaining edge cases — such as opening a results link directly or using an older " +
          "browser session. If you have access to a scenario's results, the page now simply opens.",
      },
    ],
  },
  {
    version: "0.5.3",
    date: "July 2026",
    features: [
      {
        heading: "Simulation Results Open For Every Team Member",
        description:
          "Team members granted access to simulation results can now actually open the Results page " +
          "and the comparison view. Previously these pages only loaded for administrators, even when " +
          "the right permissions had been granted.",
      },
    ],
  },
  {
    version: "0.5.2",
    date: "July 2026",
    features: [
      {
        heading: "Finer Control Over Team Permissions",
        description:
          "Project administrators can now grant every project permission from the team screens, " +
          "including the document control (Compliance) permissions, renaming simulation runs, " +
          "deleting simulation results, and updating the project banner. Previously some of these " +
          "could not be assigned to team members at all.",
      },
    ],
  },
  {
    version: "0.5.1",
    date: "July 2026",
    features: [
      {
        heading: "Approvals With Real Signatures",
        description:
          "Documents can now go round a review chain before they are issued. Reviewers sign in turn, " +
          "each seeing exactly what they are signing for, and a rejection sends the revision back to " +
          "draft with the reason attached. Where your workflow calls for it, a step can require a " +
          "proper digital signature — your name and password, recorded against the file itself.",
      },
      {
        heading: "Transmittals",
        description:
          "Send issued revisions to another company on a numbered transmittal, with a purpose and a " +
          "response date. The recipient accepts, accepts with comments, or rejects each item, and the " +
          "envelope closes itself once every item has an answer. Both sides can download the whole " +
          "package as a single zip, and an existing transmittal log can be imported alongside your " +
          "document register.",
      },
      {
        heading: "Shared Projects Stay Confidential",
        description:
          "On a project shared between companies, your documents are yours until you send them. The " +
          "other company sees a document only once you have transmitted a revision of it to them — and " +
          "then only the revision you sent. Reviewers from a partner company can still be named on an " +
          "approval step when you want them there.",
      },
      {
        heading: "Document Control, Your Way",
        description:
          "A new settings screen lets administrators shape document control around how your business " +
          "actually works — code lists, statuses, issue purposes, and the approval workflow each " +
          "document type follows. Save the arrangement as your company default and new projects start " +
          "from it.",
      },
      {
        heading: "Permissions That Match Your Access",
        description:
          "If you were granted access to documents or maintenance logs but the screens still appeared " +
          "read-only, that is fixed — the app now asks the server what you may do rather than guessing. " +
          "Leach cycle export to Parquet also works again, page descriptions sit straight under their " +
          "titles, and exported irrigation rates now respect your chosen units.",
      },
    ],
    note:
      "If you exported leach cycles with an application-rate unit other than L/h/m², the rate column " +
      "in earlier exports could be wrong by the time factor (for example 24× low under L/day/m²). " +
      "Exports made from this release are correct — it is worth re-exporting anything you still rely on.",
  },
  {
    version: "0.5.0",
    date: "July 2026",
    features: [
      {
        heading: "Document Register",
        description:
          "A new Compliance section keeps your controlled documents in one place. Register a document " +
          "once, then upload and issue each revision against it — the register shows the current " +
          "revision at a glance, and every change is recorded in a full audit trail. Existing document " +
          "logs can be brought across in bulk from a spreadsheet, and document numbers are built for you " +
          "from your project's own numbering scheme.",
      },
      {
        heading: "Climate Files Import Again",
        description:
          "Importing climate data from your own CSV or Excel file failed part-way through with an " +
          "unhelpful error. It now works for every file. If you tried to bring in your own climate " +
          "data recently and gave up, it is worth another go — importing from the Open-Meteo archive " +
          "was never affected.",
      },
      {
        heading: "Project Progress You Can Trust",
        description:
          "Progress bars on the projects list showed 0% no matter how much of the task board was " +
          "finished. They now reflect the real state of your tasks, and match the figure on the " +
          "project page.",
      },
      {
        heading: "Guidance Where You Need It",
        description:
          "Help buttons have appeared across the app, explaining the choice in front of you without " +
          "sending you looking for documentation — including which simulation model to use and what " +
          "snapshots are for. The archiving help now sits where archiving actually happens.",
      },
      {
        heading: "Better Spreadsheet Imports",
        description:
          "Imports now find the real header row on their own, so files with a title or blank rows above " +
          "the table no longer need editing first, and hidden sheets are skipped instead of being read " +
          "by mistake. Operational data columns can also carry a unit type, so your figures display in " +
          "the units you prefer.",
      },
      {
        heading: "A More Polished Interface",
        description:
          "Dialogs now open above the navigation instead of behind it, colour schemes are consistent " +
          "across the app, and the landing page stacks properly on tall screens.",
      },
    ],
    note:
      "The Document Register shows every document on a project to anyone with access to that project. " +
      "If you share a project across companies, hold off on uploading commercially sensitive documents " +
      "until per-company visibility arrives in the next update.",
  },
  {
    version: "0.4.3",
    date: "July 2026",
    features: [
      {
        heading: "Emails Read Correctly In Outlook",
        description:
          "Password resets, verifications and organization invitations were arriving washed out in " +
          "Outlook, with a hard rectangle behind the logo. They are now designed dark from the start, " +
          "so they stay legible whichever inbox and colour scheme you read them in.",
      },
      {
        heading: "A More Polished Company Page",
        description:
          "Spacing, alignment and focus outlines have been corrected across the Clients list and the " +
          "company detail page. Long email addresses now wrap instead of being cut off, project cards " +
          "line up as a row, and the page no longer picks up stray styling from wherever you browsed " +
          "before it.",
      },
      {
        heading: "Unused Company Settings Removed",
        description:
          "The primary and secondary colour pickers and the Simulation Retention field have been taken " +
          "out of Company Settings. None of them were connected to anything — no colour was ever applied " +
          "and no simulation was ever removed on a schedule. Your results are untouched and stay where " +
          "they are.",
      },
    ],
  },
  {
    version: "0.4.2",
    date: "July 2026",
    features: [
      {
        heading: "Emails From Orecasting Now Look The Part",
        description:
          "Password resets, email verifications and organization invitations arrive with the Orecasting " +
          "logo and styling instead of plain text, and they read properly on phones and in dark mode.",
      },
      {
        heading: "Revoking An Invitation Asks First",
        description:
          "Cancelling a pending invitation now confirms before it goes ahead, and names the address it " +
          "will affect — a single mis-click no longer kills someone's invite link.",
      },
      {
        heading: "Pending Invitations Fit On Screen",
        description:
          "The Resend and Revoke buttons sit below the invited address rather than beside it, so they " +
          "stay reachable on narrower screens and with longer email addresses.",
      },
      {
        heading: "Kinetics Effective Dates Save As You Go",
        description:
          "Edits to an ore property's effective dates are kept as you make them, so changes no longer " +
          "depend on how you close the editor.",
      },
    ],
  },
  {
    version: "0.4.1",
    date: "July 2026",
    features: [
      {
        heading: "Kinetics Effective Dates Now Save Correctly",
        description:
          "Deleting an extraction table from an ore property now sticks — previously it reappeared the " +
          "next time the page loaded. Adding a new effective date also keeps the dates already on that " +
          "metal instead of replacing them.",
      },
    ],
    note:
      "If you added an effective date to an ore property that already had one, its earlier extraction " +
      "tables may have been lost without any error being shown. Please open your ore properties on the " +
      "Kinetics page and check that every effective date you expect is still listed, and still has its " +
      "table data.",
  },
  {
    version: "0.4.0",
    date: "July 2026",
    features: [
      {
        heading: "Maintenance Logs",
        description:
          "Track your site equipment in a new maintenance registry — record problems, repairs and " +
          "inspections against each asset, and see open issues at a glance from the project's Operation section.",
      },
      {
        heading: "Operational Data Logs",
        description:
          "Capture daily and shift plant data in logs you define yourself, or import them from Excel. " +
          "Review recorded actuals right alongside your simulation work.",
      },
      {
        heading: "Project Timeline & Milestones",
        description:
          "The task board gains a timeline view — give tasks start dates, mark key milestones, and " +
          "watch phases roll up so the whole project schedule reads at a glance.",
      },
      {
        heading: "Invite Your Team by Email",
        description:
          "Send email invitations to bring colleagues into your company — no more sharing organization " +
          "codes by hand.",
      },
      {
        heading: "Site Operations in the Claude Integration",
        description:
          "If your company uses the Claude connection, it can now read your maintenance and operational " +
          "logs to help troubleshoot equipment and summarise plant performance.",
      },
      {
        heading: "A More Polished Interface",
        description:
          "Smoother water-balance editing on touch screens, clearer sign-in error messages, corrected " +
          "dates on imported files, and these release notes now catch you up on any updates you missed.",
      },
    ],
    note:
      "Dates in imported files previously could shift by one day depending on your timezone — " +
      "this is now fixed. If past imports looked a day off, re-importing the file will store the dates as written.",
  },
  {
    version: "0.3.0",
    date: "July 2026",
    features: [
      {
        heading: "Edit Metals In Place",
        description:
          "Rename a metal or update its details directly from the Kinetics page. " +
          "Related settings across the scenario are kept in step for you.",
      },
      {
        heading: "Dated Metal Pricing",
        description:
          "Recovered metal is now valued from your price table rather than a single fixed figure, " +
          "so scenario economics reflect the prices you have entered.",
      },
      {
        heading: "Smarter Climate Imports",
        description:
          "Imported climate files now default to your preferred units, and solar radiation has been " +
          "added to the supported measurements.",
      },
      {
        heading: "Staged Leach Schedules",
        description:
          "Add to an existing leach schedule instead of replacing it — useful for plans that build up " +
          "across multiple phases.",
      },
      {
        heading: "A More Polished Interface",
        description:
          "Improvements to charts, headings, menus and dialogs throughout the app, " +
          "including better behaviour at narrow window widths and with a pinned sidebar.",
      },
    ],
    note:
      "After upgrading, please double-check your unit selections when importing climate data, " +
      "and confirm each metal has pricing entered.",
  },
  {
    version: "0.2.0",
    date: "June 2026",
    features: [
      {
        heading: "Heap Leach Simulation",
        description:
          "Run full heap leach simulations with daily timestep physics via the Étude cloud engine. " +
          "Configure heap geometry, ore properties, hydro parameters, and leach schedules — then submit and track results in real time.",
      },
      {
        heading: "3D Heap Visualisation",
        description:
          "Explore your heap in an interactive 3D viewer. Navigate daily snapshots to see saturation, " +
          "extraction progress, and leach front propagation through the ore body over the simulation period.",
      },
      {
        heading: "Results & Export",
        description:
          "Review recovery curves, metal production totals, and water balance outputs in the Results page. " +
          "Export simulation data to Excel for further analysis or reporting.",
      },
      {
        heading: "Water Balance Editor",
        description:
          "Build and edit the facility water balance as a node-edge flow graph. " +
          "Connect heaps, ponds, and process plants to define how solution moves across the site.",
      },
      {
        heading: "Multi-Scenario Projects",
        description:
          "Organise work into projects with multiple scenarios — baseline, alternatives, and sensitivities — " +
          "all under a single project with shared access controls.",
      },
    ],
  },
];

// Numeric "x.y.z" comparison — localeCompare would put "0.10.0" before "0.9.0".
const compareVersions = (a, b) => {
  const pa = String(a).split(".").map(Number);
  const pb = String(b).split(".").map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff) return diff;
  }
  return 0;
};

const VERSION_PATTERN = /^\d+(\.\d+)*$/;

export const latestReleaseVersion = releaseNotes[0]?.version ?? null;

// Everything newer than the version the user last acknowledged, newest first.
// A user with no stored version (new browser, or one that pre-dates version
// tracking) gets only the latest entry — not the full history.
export const getUnseenReleaseNotes = (
  lastSeenVersion,
  notes = releaseNotes,
) => {
  if (notes.length === 0) return [];
  if (!lastSeenVersion || !VERSION_PATTERN.test(lastSeenVersion)) {
    return [notes[0]];
  }
  return notes.filter((r) => compareVersions(r.version, lastSeenVersion) > 0);
};

export default releaseNotes;
